import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
// src/components/ClientManager.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { getChatLeads } from '../services/chatLeadService';
import {
  CONTACT_ANALYTICS_RANGES,
  getContactAnalyticsActivity
} from '../services/contactAnalyticsService';
import { getCustomerDripHistory } from '../services/dripHistoryService';
import { decryptPassword } from '../utils/decrypt';
import { getPositiveUserId, getStoredUser } from '../utils/authUser';
import './ClientManager.css';

const CLIENT_JOURNEY_STAGES = [
  { key: 'lead', label: 'Initial Lead' },
  { key: 'discover', label: 'Discovery Call' },
  { key: 'onboard', label: 'Client Onboard' },
  { key: 'research', label: 'Market Research' },
  { key: 'offer', label: 'Make Offer' },
  { key: 'inspection', label: 'Home Inspection' },
  { key: 'closing', label: 'Closing Process' },
  { key: 'complete', label: 'Deal Complete' }
];

const CONTACT_WORKSPACE_TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'journey', label: 'Client Journey' },
  { key: 'interests', label: 'Interests' },
  { key: 'marketing', label: 'Marketing' },
  { key: 'activity', label: 'Website Activity' }
];

const getContactRouteKey = (email = '') => {
  let hash = 2166136261;
  const normalized = email.trim().toLowerCase();
  for (let index = 0; index < normalized.length; index += 1) {
    hash ^= normalized.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

const WorkspaceIcon = ({ name }) => {
  const paths = {
    overview: <><path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z" /></>,
    journey: <><path d="M5 19V8m7 11V5m7 14v-7" /><path d="M3 19h18" /></>,
    interests: <><path d="M12 20s-7-4.3-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.7-7 10-7 10Z" /></>,
    marketing: <><path d="m4 13 3 1 9-5V5L7 10H4zM7 14l1.5 5h3L10 13" /><path d="M18 8.5h2M18 5l1.5-1.5M18 12l1.5 1.5" /></>,
    activity: <><path d="M4 17l4-5 4 3 6-8" /><path d="M18 7h-4m4 0v4" /></>,
    mail: <><rect x="3.5" y="5" width="17" height="14" rx="2" /><path d="m5 7 7 6 7-6" /></>,
    phone: <><path d="M7 4.5h3l1.5 4-2 1.5a14 14 0 0 0 4.5 4.5l1.5-2 4 1.5v3a2 2 0 0 1-2 2C10.1 19 5 13.9 5 7a2 2 0 0 1 2-2.5Z" /></>,
    edit: <><path d="m14 5 5 5M4 20l4.2-1 10-10a2.1 2.1 0 0 0-3-3l-10 10L4 20Z" /></>,
    trash: <><path d="M4 7h16M9 7V4h6v3m-9 0 1 13h10l1-13M10 11v5m4-5v5" /></>,
    back: <><path d="m15 18-6-6 6-6" /></>
  };
  return <svg viewBox="0 0 24 24" aria-hidden="true">{paths[name]}</svg>;
};

const normalizeJourneyStatus = (status) => {
  if (status === 'Completed') return 'completed';
  if (status === 'In-Progress') return 'in-progress';
  if (status === 'Blocked') return 'blocked';
  return 'pending';
};

const JourneyStageIcon = ({ stageKey, completed }) => {
  if (completed) {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="m6.5 12.5 3.25 3.25L17.5 8" />
      </svg>
    );
  }

  const icons = {
    lead: <><path d="M7 4.5h3l1.5 4-2 1.5a14 14 0 0 0 4.5 4.5l1.5-2 4 1.5v3a2 2 0 0 1-2 2C10.1 19 5 13.9 5 7a2 2 0 0 1 2-2.5Z" /></>,
    discover: <><circle cx="10.5" cy="10.5" r="5.5" /><path d="m15 15 4 4" /></>,
    onboard: <><path d="M8 12a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm8-1a2.5 2.5 0 1 0 0-5" /><path d="M3.5 19a4.5 4.5 0 0 1 9 0m1-5a4 4 0 0 1 6.5 3.1" /></>,
    research: <><path d="M5 19V9m7 10V5m7 14v-7" /><path d="M3 19h18" /></>,
    offer: <><path d="M4 8.5 12 4l8 4.5-8 4.5-8-4.5Z" /><path d="M7 11v5.5c2.8 2 7.2 2 10 0V11" /></>,
    inspection: <><path d="m4 11 8-7 8 7" /><path d="M6.5 9.5V20h11V9.5M9 20v-6h6v6" /></>,
    closing: <><path d="M5 10h14v10H5zM8 10V7a4 4 0 0 1 8 0v3" /><path d="M12 14v2" /></>,
    complete: <><path d="M12 3.5 14.3 8l5 .7-3.6 3.5.8 5-4.5-2.4-4.5 2.4.8-5-3.6-3.5 5-.7L12 3.5Z" /></>
  };

  return <svg viewBox="0 0 24 24" aria-hidden="true">{icons[stageKey]}</svg>;
};

const emptyAiDripRows = () => [
  { prompt: '', date: '', html: '', subject: '', generating: false, generated: false, generatedScope: '', error: '' },
  { prompt: '', date: '', html: '', subject: '', generating: false, generated: false, generatedScope: '', error: '' },
  { prompt: '', date: '', html: '', subject: '', generating: false, generated: false, generatedScope: '', error: '' }
];

const formatDripDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const DRIP_STATUS_LABELS = {
  ACTIVE: 'Active',
  COMPLETED: 'Completed',
  UNSUBSCRIBED: 'Unsubscribed',
  PENDING: 'Pending',
  SENDING: 'Sending',
  SENT: 'Sent',
  FAILED: 'Retrying',
  FAILED_PERMANENT: 'Failed',
  CANCELLED: 'Cancelled'
};

const dripStatusLabel = (value) => DRIP_STATUS_LABELS[String(value || '').toUpperCase()]
  || String(value || 'Unknown').replaceAll('_', ' ');

const dripStatusTone = (value) => {
  const status = String(value || '').toUpperCase();
  if (['SENT', 'COMPLETED'].includes(status)) return 'success';
  if (['ACTIVE', 'PENDING', 'SENDING'].includes(status)) return 'active';
  if (status === 'FAILED') return 'warning';
  if (['FAILED_PERMANENT', 'CANCELLED', 'UNSUBSCRIBED'].includes(status)) return 'stopped';
  return 'neutral';
};

const historyDateValue = (...values) => {
  for (const value of values) {
    if (!value) continue;
    const normalized = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value;
    const date = new Date(normalized);
    if (!Number.isNaN(date.getTime())) return date;
  }
  return null;
};

const formatHistoryDate = (...values) => {
  const date = historyDateValue(...values);
  return date ? date.toLocaleString() : 'Time unavailable';
};

const dripStepName = (step) => step?.subject || step?.templateName || `Email ${step?.stepNumber || ''}`.trim();

const formatCampaignName = (value) => String(value || 'Drip campaign')
  .replaceAll('_', ' ')
  .replaceAll('-', ' ')
  .replace(/\b\w/g, letter => letter.toUpperCase());

const emptySubForm = () => ({ cadence: 'daily', city: '', state: SITE_CONFIG.STATE, zip: '', county: '', minPrice: '', maxPrice: '', minBedrooms: '', maxBedrooms: '', minBathrooms: '', maxBathrooms: '', minSqft: '', maxSqft: '', propertyType: '', propertySubType: '', stories: '', minYearBuilt: '', maxYearBuilt: '', acreage: '', foundation: '', schoolDistrict: '', hasMedia: false, hasFence: false, hasPool: false, communityFeatures: [] });

const showingAction = (interest) => interest?.showingRequestStatus === 'requested'
  ? 'Review & Confirm Showing'
  : interest?.scheduledShowing ? 'Reschedule Showing' : 'Schedule Showing';

const ClientManager = () => {
  const navigate = useNavigate();
  const { contactKey } = useParams();
  const isContactWorkspace = Boolean(contactKey);
  const currentUser = getStoredUser();
  const currentUserId = getPositiveUserId(currentUser);
  const currentUserToken = currentUser.token;
  const currentUserTokenType = currentUser.type;
  const isLead = currentUser.roles?.includes('ROLE_LEAD');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [chatLeads, setChatLeads] = useState([]);
  const [loadingChatLeads, setLoadingChatLeads] = useState(true);
  const [siteUsers, setSiteUsers] = useState([]);
  const [loadingSiteUsers, setLoadingSiteUsers] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactInterests, setContactInterests] = useState(null);
  const [loadingInterests, setLoadingInterests] = useState(false);
  const [contactProgress, setContactProgress] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingContact, setAddingContact] = useState(false);
  const [newContact, setNewContact] = useState({
    Name: '',
    CompanyName: '',
    Address1: '',
    Address2: '',
    City: '',
    State: '',
    Zip: '',
    PhoneNumber: '',
    Email: '',
    Group: '',
    interest_area: '',
    price_target: '',
    assigned_agent: ''
  });
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [updatingStage, setUpdatingStage] = useState(false);
  const [currentStage, setCurrentStage] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('In-Progress');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [sendSMS, setSendSMS] = useState(false);
  const [schedulingShowing, setSchedulingShowing] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showSellingDetailsModal, setShowSellingDetailsModal] = useState(false);
  const [sellingDetailsForm, setSellingDetailsForm] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    address: '',
    size: '',
    condition: '',
    timeline: '',
    listed: false,
    listingKey: '',
    status: ''
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [currentSellingInquiry, setCurrentSellingInquiry] = useState(null);
  const [submittingDetails, setSubmittingDetails] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editContact, setEditContact] = useState({
    Name: '',
    CompanyName: '',
    Address1: '',
    Address2: '',
    City: '',
    State: '',
    Zip: '',
    PhoneNumber: '',
    Email: '',
    interest_area: '',
    price_target: '',
    assigned_agent: ''
  });
  const [updatingContact, setUpdatingContact] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [chatLeadSearch, setChatLeadSearch] = useState('');
  const [siteUserSearch, setSiteUserSearch] = useState('');
  const [showAddSuccess, setShowAddSuccess] = useState(false);
  const [showOpenHouseModal, setShowOpenHouseModal] = useState(false);
  const [openHouseMlsKey, setOpenHouseMlsKey] = useState('');
  const [submittingOpenHouse, setSubmittingOpenHouse] = useState(false);
  const [showQRFullscreen, setShowQRFullscreen] = useState(false);
  const [showOpenHouseSuccess, setShowOpenHouseSuccess] = useState(false);
  const [currentMlsKey, setCurrentMlsKey] = useState('');
  const [loadingCurrentKey, setLoadingCurrentKey] = useState(false);
  const [contactSubscriptions, setContactSubscriptions] = useState([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [showSubForm, setShowSubForm] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const [subSendLogs, setSubSendLogs] = useState([]);
  const [subFormSuccess, setSubFormSuccess] = useState(false);
  const [contactDripHistory, setContactDripHistory] = useState(null);
  const [contactDripStatus, setContactDripStatus] = useState('idle');
  const [contactDripError, setContactDripError] = useState(null);
  const [showAiDripModal, setShowAiDripModal] = useState(false);
  const [aiDripRows, setAiDripRows] = useState(emptyAiDripRows);
  const [aiDripCadence, setAiDripCadence] = useState('');
  const [aiDripStartDate, setAiDripStartDate] = useState('');
  const [schedulingAiDrip, setSchedulingAiDrip] = useState(false);
  const [aiDripSuccess, setAiDripSuccess] = useState(false);
  const [aiDripError, setAiDripError] = useState('');
  const [aiDripPreviewIndex, setAiDripPreviewIndex] = useState(null);
  const [aiDripScope, setAiDripScope] = useState(currentUserId ? 'agent' : 'brokerage');
  const [senderEmail, setSenderEmail] = useState('');
  const [senderPassword, setSenderPassword] = useState('');
  const [subForm, setSubForm] = useState(emptySubForm());
  const [contactAnalyticsRange, setContactAnalyticsRange] = useState('30d');
  const [contactAnalytics, setContactAnalytics] = useState(null);
  const [contactAnalyticsStatus, setContactAnalyticsStatus] = useState('idle');
  const [contactAnalyticsError, setContactAnalyticsError] = useState('');
  const [activeContactTab, setActiveContactTab] = useState('overview');

  useEffect(() => {
    if (!selectedContact?.Email) {
      setContactAnalytics(null);
      setContactAnalyticsStatus('idle');
      setContactAnalyticsError('');
      return undefined;
    }
    if (loadingSiteUsers) {
      setContactAnalyticsStatus('loading');
      return undefined;
    }

    const selectedEmail = selectedContact.Email.trim().toLowerCase();
    const registeredUser = siteUsers.find(
      user => user.email?.trim().toLowerCase() === selectedEmail
    );
    if (!registeredUser?.id && loadingInterests) {
      setContactAnalyticsStatus('loading');
      return undefined;
    }
    const linkedChatLead = !registeredUser?.id
      ? contactInterests?.chatLeads?.find(lead => Number.isInteger(Number(lead.id)) && Number(lead.id) > 0)
      : null;
    if (!registeredUser?.id && !linkedChatLead?.id) {
      setContactAnalytics(null);
      setContactAnalyticsStatus('unregistered');
      setContactAnalyticsError('');
      return undefined;
    }

    const controller = new AbortController();
    setContactAnalyticsStatus('loading');
    setContactAnalyticsError('');
    getContactAnalyticsActivity({
      user: { token: currentUserToken, type: currentUserTokenType },
      targetUserId: registeredUser?.id,
      chatLeadId: linkedChatLead?.id,
      range: contactAnalyticsRange,
      signal: controller.signal
    })
      .then(data => {
        setContactAnalytics(data);
        setContactAnalyticsStatus('ready');
      })
      .catch(error => {
        if (error.name === 'AbortError') return;
        setContactAnalytics(null);
        setContactAnalyticsStatus('error');
        setContactAnalyticsError(error.message || 'Contact analytics are temporarily unavailable.');
      });

    return () => controller.abort();
  }, [
    contactAnalyticsRange,
    currentUserToken,
    currentUserTokenType,
    contactInterests,
    loadingInterests,
    loadingSiteUsers,
    selectedContact,
    siteUsers
  ]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setSelectedFile(file);
      setUploadSuccess(false);
    } else {
      alert('Please select a CSV file');
    }
  };

  const fetchSingleContact = async (email) => {
    try {
      const response = await fetch(`https://tophamtechbe.com/treas-postgrid/s3/get-address/${SITE_CONFIG.SITE_ID}?email=${encodeURIComponent(email)}`, {
        headers: { 'X-API-Key': 'treas-api-key-2025' }
      });
      if (response.ok) {
        const data = await response.json();
        return data.contact || data;
      }
    } catch (error) {
      console.error('Error fetching single contact:', error);
    }
    return null;
  };

  const fetchContacts = async () => {
    try {
      const response = await fetch(`https://tophamtechbe.com/treas-postgrid/s3/get-address-list/${SITE_CONFIG.SITE_ID}`, {
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });

      if (response.ok) {
        const data = await response.json();
        let allContacts = data.contacts || [];
        if (isLead) {
          allContacts = allContacts.filter(c => !c.assigned_agent || c.assigned_agent === currentUser.username);
        }
        setContacts(allContacts);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoadingContacts(false);
    }
  };

  const fetchChatLeads = async () => {
    try {
      const response = await getChatLeads({ siteId: SITE_CONFIG.SITE_ID });

      if (response.ok) {
        const data = await response.json();
        let allLeads = data || [];
        if (isLead) {
          allLeads = allLeads.filter(l => !l.assignedAgentId || l.assignedAgentId === currentUser.id);
        }
        setChatLeads(allLeads);
      }
    } catch (error) {
      console.error('Error fetching chat leads:', error);
    } finally {
      setLoadingChatLeads(false);
    }
  };

  const fetchSiteUsers = async () => {
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/site-users?siteId=${SITE_CONFIG.SITE_ID}`, {
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSiteUsers(data || []);
      }
    } catch (error) {
      console.error('Error fetching site users:', error);
    } finally {
      setLoadingSiteUsers(false);
    }
  };

  const toggleOnLeave = async (e, user) => {
    e.stopPropagation();
    const newStatus = !user.onLeave;
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/site-users/on-leave?id=${user.id}&onLeave=${newStatus}`, {
        method: 'POST',
        headers: { 'X-API-Key': 'treas-api-key-2025' }
      });
      if (response.ok) {
        setSiteUsers(prev => prev.map(u => u.id === user.id ? { ...u, onLeave: newStatus } : u));
      }
    } catch (error) {
      console.error('Error toggling on-leave:', error);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('site_id', SITE_CONFIG.SITE_ID);

      const response = await fetch('https://tophamtechbe.com/treas-postgrid/s3/address', {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        },
        body: formData
      });

      if (response.ok) {
        setUploadSuccess(true);
        setSelectedFile(null);
        fetchContacts(); // Refresh contacts list
      } else {
        alert('Upload failed. Please try again.');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const fetchContactInterests = async (email) => {
    setLoadingInterests(true);
    try {
      const [buyResponse, sellResponse, sellInquiryResponse, chatLeadResponse] = await Promise.all([
        fetch(`https://tophamtechbe.com/api/treas/idx/v1/interested-buy?email=${encodeURIComponent(email)}`, {
          headers: { 'X-API-Key': 'treas-api-key-2025' }
        }),
        fetch(`https://tophamtechbe.com/api/treas/idx/v1/selling-details?email=${encodeURIComponent(email)}`, {
          headers: { 'X-API-Key': 'treas-api-key-2025' }
        }),
        fetch(`https://tophamtechbe.com/api/treas/idx/v1/selling-inquiry?email=${encodeURIComponent(email)}&siteId=${SITE_CONFIG.SITE_ID}`, {
          headers: { 'X-API-Key': 'treas-api-key-2025' }
        }),
        getChatLeads({ email, siteId: SITE_CONFIG.SITE_ID })
      ]);

      const buyData = buyResponse.ok ? await buyResponse.json() : [];
      const sellData = sellResponse.ok ? await sellResponse.json() : [];
      const sellInquiryData = sellInquiryResponse.ok ? await sellInquiryResponse.json() : [];
      const chatLeadData = chatLeadResponse.ok ? await chatLeadResponse.json() : [];

      // Mark selling inquiries vs selling details
      const markedSellData = (Array.isArray(sellData) ? sellData : []).map(item => ({...item, isSellingDetail: true}));
      const markedSellInquiryData = (Array.isArray(sellInquiryData) ? sellInquiryData : []).map(item => ({...item, isSellingInquiry: true}));

      const combinedSelling = [...markedSellData, ...markedSellInquiryData];

      setContactInterests({
        buying: Array.isArray(buyData) ? buyData : [],
        selling: combinedSelling,
        chatLeads: Array.isArray(chatLeadData) ? chatLeadData : []
      });
    } catch (error) {
      console.error('Error fetching contact interests:', error);
      setContactInterests({ buying: null, selling: null });
    } finally {
      setLoadingInterests(false);
    }
  };

  const renderShowingControls = (interest) => (
    <div className="showing-section">
      <button
        type="button"
        className="schedule-showing-btn"
        disabled={schedulingShowing}
        onClick={() => {
          const showing = interest.showingRequestStatus === 'requested'
            ? interest.requestedShowing : interest.scheduledShowing;
          setScheduleDate(showing ? String(showing).slice(0, 10) : '');
          setScheduleTime(showing ? String(showing).slice(11, 16) : '');
          setSendSMS(false);
          setContactInterests(prev => ({ ...prev, selectedBuying: interest }));
          setShowScheduleModal(true);
        }}
      >
        📅 {showingAction(interest)}
      </button>
      {interest.showingRequestStatus === 'requested' && (
        <p className="showing-request-pending">
          <strong>Showing requested · awaiting your confirmation</strong><br />
          {String(interest.requestedShowing).replace('T', ' ')} · {interest.showingTimezone}<br />
          {interest.showingName} · {interest.showingEmail} · {interest.showingPhone}
        </p>
      )}
      {interest.scheduledShowing && (
        <span className="scheduled-date">
          Confirmed: {String(interest.scheduledShowing).replace('T', ' ').slice(0, 16)}
          {interest.showingTimezone ? ` · ${interest.showingTimezone}` : ''}
        </span>
      )}
    </div>
  );

  const fetchContactSubscriptions = async (email) => {
    setLoadingSubscriptions(true);
    setContactSubscriptions([]);
    setSubSendLogs([]);
    setContactDripHistory(null);
    setContactDripStatus('loading');
    setContactDripError(null);
    try {
      const [subscriptionsResult, logsResult, historyResult] = await Promise.allSettled([
        fetch(`https://tophamtechbe.com/api/treas/comm/property-search-subscriptions?siteId=${SITE_CONFIG.SITE_ID}&emailAddress=${encodeURIComponent(email)}`, { headers: { 'X-API-Key': 'treas-api-key-2025' } })
          .then(response => response.ok ? response.json() : []),
        fetch(`https://tophamtechbe.com/api/treas/comm/property-search-send-log?siteId=${SITE_CONFIG.SITE_ID}&emailAddress=${encodeURIComponent(email)}`, { headers: { 'X-API-Key': 'treas-api-key-2025' } })
          .then(response => response.ok ? response.json() : []),
        getCustomerDripHistory({
          user: { token: currentUserToken, type: currentUserTokenType },
          siteId: currentUser.siteId,
          emailAddress: email
        })
      ]);

      if (subscriptionsResult.status === 'fulfilled') {
        setContactSubscriptions(Array.isArray(subscriptionsResult.value) ? subscriptionsResult.value : []);
      }
      if (logsResult.status === 'fulfilled') {
        setSubSendLogs(Array.isArray(logsResult.value) ? logsResult.value : []);
      }
      if (historyResult.status === 'fulfilled') {
        const history = historyResult.value;
        setContactDripHistory(history);
        setContactDripStatus(Number(history.enrollmentCount) > 0 ? 'ready' : 'empty');
      } else {
        const historyError = historyResult.reason;
        if (historyError?.status === 401) {
          localStorage.removeItem('user');
          sessionStorage.removeItem('user');
          navigate('/', { replace: true });
          return;
        }
        setContactDripStatus('error');
        setContactDripError({
          status: historyError?.status,
          message: historyError?.message || 'Customer email history is temporarily unavailable.'
        });
      }
    } catch (e) {
      console.error('Error fetching subscriptions:', e);
      setContactDripStatus('error');
      setContactDripError({ message: 'Customer marketing history is temporarily unavailable.' });
    }
    setLoadingSubscriptions(false);
  };

  const fetchSenderConfig = async () => {
    try {
      const res = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/feature-flag?siteId=${SITE_CONFIG.SITE_ID}`, { headers: { 'X-API-Key': 'treas-api-key-2025' } });
      if (res.ok) {
        const data = await res.json();
        if (data.senderEmail) setSenderEmail(data.senderEmail);
        if (data.senderEmailPassword) {
          const pw = await decryptPassword(data.senderEmailPassword);
          if (pw) setSenderPassword(pw);
        }
      }
    } catch (e) {}
  };

  const updateAiDripRow = (index, patch) => {
    setAiDripRows(prev => prev.map((row, i) => i === index ? { ...row, ...patch } : row));
  };

  const getCommApiError = async (response, fallback) => {
    try {
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const data = await response.json();
        if (data.code === 'AGENT_PROFILE_NOT_FOUND') {
          return 'Your email profile is missing or inactive. Please contact an administrator.';
        }
        return data.message || fallback;
      }
      return (await response.text()) || fallback;
    } catch (error) {
      return fallback;
    }
  };

  const handleAiDripScopeChange = (scope) => {
    if (scope === aiDripScope || aiDripRows.some(row => row.generating)) return;
    setAiDripScope(scope);
    setAiDripPreviewIndex(null);
    setAiDripError('');
    setAiDripRows(prev => prev.map(row => ({
      ...row,
      html: '',
      subject: '',
      generating: false,
      generated: false,
      generatedScope: '',
      error: ''
    })));
  };

  const applyAiDripCadence = (cadence, startDate) => {
    const start = startDate || aiDripStartDate;
    if (!start) return;
    const base = new Date(`${start}T12:00:00`);
    const step = cadence === 'weekly' ? 7 : 30;
    setAiDripRows(prev => prev.map((row, i) => {
      const d = new Date(base);
      d.setDate(base.getDate() + i * step);
      return { ...row, date: formatDripDate(d) };
    }));
  };

  const handleAiDripCadenceToggle = (cadence) => {
    const next = aiDripCadence === cadence ? '' : cadence;
    setAiDripCadence(next);
    if (next) applyAiDripCadence(next);
  };

  const openAiDripModal = () => {
    setAiDripRows(emptyAiDripRows());
    setAiDripCadence('');
    setAiDripStartDate(formatDripDate(new Date()));
    setAiDripError('');
    setAiDripSuccess(false);
    setAiDripPreviewIndex(null);
    setAiDripScope(currentUserId ? 'agent' : 'brokerage');
    setShowAiDripModal(true);
  };

  const generateAiDripEmail = async (index) => {
    const row = aiDripRows[index];
    if (!row.prompt.trim()) return;
    updateAiDripRow(index, { generating: true, error: '' });
    try {
      if (aiDripScope === 'agent' && !currentUserId) {
        throw new Error('Your user account is missing a valid ID. Please contact an administrator.');
      }
      const body = {
        siteId: SITE_CONFIG.SITE_ID,
        request: row.prompt,
        generationMode: 'agent_marketing',
        researchMode: 'auto'
      };
      if (aiDripScope === 'agent') body.userId = currentUserId;
      const firstName = (selectedContact?.Name || '').split(' ')[0];
      if (firstName) body.recipientName = firstName;

      const response = await fetch('https://tophamtechbe.com/api/treas/comm/generate-ai-template', {
        method: 'POST',
        headers: { 'X-API-Key': 'treas-api-key-2025', 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!response.ok) throw new Error(await getCommApiError(response, 'Failed to generate email'));
      const data = await response.json();
      if (!data.html) throw new Error('No HTML returned from generator');
      updateAiDripRow(index, { generating: false, generated: true, generatedScope: aiDripScope, html: data.html, subject: data.subject || '' });
    } catch (e) {
      updateAiDripRow(index, { generating: false, generated: false, error: `Email ${index + 1}: ${e.message}` });
    }
  };

  const scheduleAiDrip = async () => {
    const email = selectedContact.Email || selectedContact.email;
    const activeRows = aiDripRows.filter(r => r.prompt.trim() || r.date || r.html);
    if (activeRows.length === 0 || !activeRows.every(r => r.generated && r.generatedScope === aiDripScope && r.date)) return;

    setSchedulingAiDrip(true);
    setAiDripError('');

    try {
      if (aiDripScope === 'agent' && !currentUserId) {
        throw new Error('Your user account is missing a valid ID. Please contact an administrator.');
      }
      // Store each generated email using the same ownership used for generation.
      const templateNames = [];
      for (let i = 0; i < activeRows.length; i++) {
        const row = activeRows[i];
        const name = `AI Drip ${i + 1}: ${row.subject || 'Email'}`.slice(0, 80);
        templateNames.push(name);
        const fd = new FormData();
        fd.append('templateName', name);
        fd.append('subject', row.subject || name);
        fd.append('siteId', SITE_CONFIG.SITE_ID);
        fd.append('body', row.html);
        fd.append('scope', aiDripScope);
        if (aiDripScope === 'agent') fd.append('ownerUserId', String(currentUserId));
        if (currentUserId) fd.append('createdByUserId', String(currentUserId));
        const storeRes = await fetch('https://tophamtechbe.com/api/treas/comm/store-template', {
          method: 'POST',
          headers: { 'X-API-Key': 'treas-api-key-2025' },
          body: fd
        });
        if (!storeRes.ok) throw new Error(await getCommApiError(storeRes, 'Failed to store generated template'));
      }

      // store-template returns no ID, so look the templates up by name
      const templateParams = new URLSearchParams({ siteId: SITE_CONFIG.SITE_ID });
      if (currentUserId) templateParams.set('userId', String(currentUserId));
      const templatesRes = await fetch(`https://tophamtechbe.com/api/treas/comm/templates?${templateParams}`, {
        headers: { 'X-API-Key': 'treas-api-key-2025' }
      });
      if (!templatesRes.ok) throw new Error('Failed to load templates');
      const allTemplates = await templatesRes.json();

      const entries = activeRows.map((row, i) => {
        const match = (Array.isArray(allTemplates) ? allTemplates : []).find(t => {
          const templateScope = t.scope || 'brokerage';
          const ownerMatches = aiDripScope === 'brokerage' || Number(t.ownerUserId) === currentUserId;
          return t.templateName === templateNames[i] && templateScope === aiDripScope && ownerMatches;
        });
        if (!match) throw new Error('Could not find stored template');
        return { templateId: match.id, date: row.date };
      });

      const formData = new FormData();
      formData.append('emailaddresses', email);
      formData.append('siteId', SITE_CONFIG.SITE_ID);
      if (senderEmail) formData.append('email', senderEmail);
      if (senderPassword) formData.append('emailPassword', senderPassword);
      entries.forEach((entry, i) => {
        formData.append(`templateId${i + 1}`, entry.templateId);
        formData.append(`dripDate${i + 1}`, entry.date);
      });

      const scheduleRes = await fetch('https://tophamtechbe.com/api/treas/comm/schedule-drip', {
        method: 'POST',
        headers: { 'X-API-Key': 'treas-api-key-2025' },
        body: formData
      });
      if (!scheduleRes.ok) throw new Error('Failed to schedule drip campaign');

      setAiDripSuccess(true);
      fetchContactSubscriptions(email);
      setTimeout(() => setShowAiDripModal(false), 2000);
    } catch (e) {
      console.error('Error scheduling AI drip:', e);
      setAiDripError(e.message || 'Error scheduling AI drip');
    }

    setSchedulingAiDrip(false);
  };

  const fetchContactProgress = async (email) => {
    setLoadingProgress(true);
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/crm?email=${encodeURIComponent(email)}&siteId=${SITE_CONFIG.SITE_ID}`, {
        headers: { 'X-API-Key': 'treas-api-key-2025' }
      });

      if (response.ok) {
        const data = await response.json();
        setContactProgress(data);
      } else {
        setContactProgress(null);
      }
    } catch (error) {
      console.error('Error fetching contact progress:', error);
      setContactProgress(null);
    } finally {
      setLoadingProgress(false);
    }
  };

  const handleContactClick = (contact) => {
    setContactAnalyticsRange('30d');
    setActiveContactTab('overview');
    setContactInterests(null);
    setSelectedContact(contact);
    if (contact.Email) {
      fetchContactInterests(contact.Email);
      fetchContactProgress(contact.Email);
      fetchContactSubscriptions(contact.Email);
      navigate(`/admin/client-manager/contact/${getContactRouteKey(contact.Email)}`);
    }
  };

  useEffect(() => {
    if (!isContactWorkspace) {
      setSelectedContact(null);
      setActiveContactTab('overview');
      return;
    }
    if (loadingContacts) return;

    const routeContact = contacts.find(
      contact => contact.Email && getContactRouteKey(contact.Email) === contactKey
    );
    if (!routeContact || selectedContact?.Email === routeContact.Email) return;

    setContactAnalyticsRange('30d');
    setActiveContactTab('overview');
    setContactInterests(null);
    setSelectedContact(routeContact);
    fetchContactInterests(routeContact.Email);
    fetchContactProgress(routeContact.Email);
    fetchContactSubscriptions(routeContact.Email);
  }, [contactKey, contacts, isContactWorkspace, loadingContacts]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeContactWorkspace = () => {
    setSelectedContact(null);
    setContactInterests(null);
    setContactProgress(null);
    setActiveContactTab('overview');
    navigate('/admin/client-manager');
  };

  const handleChatLeadClick = (lead) => {
    setSelectedContact(lead);
    if (lead.email) {
      fetchContactInterests(lead.email);
      fetchContactProgress(lead.email);
      fetchContactSubscriptions(lead.email);
    }
  };

  const handleSiteUserClick = (user) => {
    setSelectedContact(user);
    if (user.email) {
      fetchContactInterests(user.email);
      fetchContactProgress(user.email);
      fetchContactSubscriptions(user.email);
    }
  };

  const handleAddLeadAsContact = async (e, lead) => {
    e.stopPropagation();

    try {
      const response = await fetch('https://tophamtechbe.com/treas-postgrid/s3/add-address', {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          site_id: SITE_CONFIG.SITE_ID,
          address: {
            Name: lead.name || '',
            CompanyName: '',
            Address1: '',
            Address2: '',
            City: '',
            State: '',
            Zip: lead.postalCode || '',
            PhoneNumber: lead.phoneNumber || '',
            Email: lead.email || '',
            Group: 'buyer',
            interest_area: lead.area || '',
            price_target: lead.price || ''
          }
        })
      });

      if (response.ok) {
        fetchContacts(); // Refresh contacts list
        setShowAddSuccess(true);
        setTimeout(() => setShowAddSuccess(false), 3000);
      } else {
        alert('Failed to add contact. Please try again.');
      }
    } catch (error) {
      console.error('Error adding lead as contact:', error);
      alert('Failed to add contact. Please try again.');
    }
  };

  const handleAddUserAsContact = async (e, user) => {
    e.stopPropagation();

    try {
      const response = await fetch('https://tophamtechbe.com/treas-postgrid/s3/add-address', {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          site_id: SITE_CONFIG.SITE_ID,
          address: {
            Name: user.name || user.username || '',
            CompanyName: '',
            Address1: '',
            Address2: '',
            City: '',
            State: '',
            Zip: '',
            PhoneNumber: user.phoneNumber || '',
            Email: user.email || ''
          }
        })
      });

      if (response.ok) {
        fetchContacts(); // Refresh contacts list
        setShowAddSuccess(true);
        setTimeout(() => setShowAddSuccess(false), 3000);
      } else {
        alert('Failed to add contact. Please try again.');
      }
    } catch (error) {
      console.error('Error adding user as contact:', error);
      alert('Failed to add contact. Please try again.');
    }
  };

  const handleEditClick = async (e, contact) => {
    e.stopPropagation();
    setEditingContact(contact);
    const emailAddr = contact.Email || contact.email;
    const fresh = emailAddr ? await fetchSingleContact(emailAddr) : null;
    const data = fresh || contact;
    setEditContact({
      Name: data.Name || data.name || data.username || '',
      CompanyName: data.CompanyName || '',
      Address1: data.Address1 || '',
      Address2: data.Address2 || '',
      City: data.City || '',
      State: data.State || '',
      Zip: data.Zip || data.postalCode || '',
      PhoneNumber: data.PhoneNumber || data.phoneNumber || '',
      Email: data.Email || data.email || '',
      Group: data.Group || '',
      interest_area: data.interest_area || '',
      price_target: data.price_target || '',
      assigned_agent: data.assigned_agent || ''
    });
    setShowEditModal(true);
  };

  const handleDeleteClick = (e, contact) => {
    e.stopPropagation();
    setContactToDelete(contact);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!contactToDelete?.Email) return;

    setDeleting(true);
    try {
      const response = await fetch('https://tophamtechbe.com/treas-postgrid/s3/remove-by-email', {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          site_id: SITE_CONFIG.SITE_ID,
          email: contactToDelete.Email
        })
      });

      if (response.ok) {
        fetchContacts(); // Refresh contacts list
        if (selectedContact?.Email === contactToDelete.Email) {
          setSelectedContact(null);
          setContactInterests(null);
          if (isContactWorkspace) navigate('/admin/client-manager');
        }
      } else {
        alert('Failed to delete contact. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      alert('Failed to delete contact. Please try again.');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setContactToDelete(null);
    }
  };

  const handleAddContact = async () => {
    if (!newContact.Name || !newContact.Email) {
      alert('Name and Email are required.');
      return;
    }

    setAddingContact(true);
    try {
      const response = await fetch('https://tophamtechbe.com/treas-postgrid/s3/add-address', {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          site_id: SITE_CONFIG.SITE_ID,
          address: newContact
        })
      });

      if (response.ok) {
        fetchContacts(); // Refresh contacts list
        setShowAddModal(false);
        setNewContact({
          Name: '',
          CompanyName: '',
          Address1: '',
          Address2: '',
          City: '',
          State: '',
          Zip: '',
          PhoneNumber: '',
          Email: '',
          Group: '',
          interest_area: '',
          price_target: '',
          assigned_agent: ''
        });
      } else {
        alert('Failed to add contact. Please try again.');
      }
    } catch (error) {
      console.error('Error adding contact:', error);
      alert('Failed to add contact. Please try again.');
    } finally {
      setAddingContact(false);
    }
  };

  const fetchCurrentMlsKey = async () => {
    setLoadingCurrentKey(true);
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/feature-flag?siteId=${SITE_CONFIG.SITE_ID}`, {
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentMlsKey(data.openHouseMlsKey || '');
      }
    } catch (error) {
      console.error('Error fetching current MLS key:', error);
    } finally {
      setLoadingCurrentKey(false);
    }
  };

  const handleSubmitOpenHouse = async () => {
    if (!openHouseMlsKey.trim()) {
      alert('Please enter an MLS key');
      return;
    }

    setSubmittingOpenHouse(true);
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/feature-flag?siteId=${SITE_CONFIG.SITE_ID}&openHouseMlsKey=${encodeURIComponent(openHouseMlsKey)}`, {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });

      if (response.ok) {
        setShowOpenHouseModal(false);
        setOpenHouseMlsKey('');
        setShowOpenHouseSuccess(true);
        setTimeout(() => setShowOpenHouseSuccess(false), 3000);
      } else {
        alert('Failed to set open house MLS key. Please try again.');
      }
    } catch (error) {
      console.error('Error setting open house MLS key:', error);
      alert('Failed to set open house MLS key. Please try again.');
    } finally {
      setSubmittingOpenHouse(false);
    }
  };

  const handleUpdateContact = async () => {
    if (!editContact.Name || !editContact.Email) {
      alert('Name and Email are required.');
      return;
    }

    setUpdatingContact(true);
    try {
      const response = await fetch('https://tophamtechbe.com/treas-postgrid/s3/update-address', {
        method: 'PUT',
        headers: {
          'X-API-Key': 'treas-api-key-2025',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          site_id: SITE_CONFIG.SITE_ID,
          address_index: editingContact.index,
          address: editContact
        })
      });

      if (response.ok) {
        const isEditingOpenContact = selectedContact?.Email === editingContact?.Email;
        if (isEditingOpenContact) {
          const updatedContact = { ...selectedContact, ...editContact, index: editingContact.index };
          setSelectedContact(updatedContact);
          fetchContactInterests(editContact.Email);
          fetchContactProgress(editContact.Email);
          fetchContactSubscriptions(editContact.Email);
          if (isContactWorkspace) {
            navigate(`/admin/client-manager/contact/${getContactRouteKey(editContact.Email)}`, { replace: true });
          }
        }
        fetchContacts(); // Refresh contacts list
        setShowEditModal(false);
        setEditingContact(null);
      } else {
        alert('Failed to update contact. Please try again.');
      }
    } catch (error) {
      console.error('Error updating contact:', error);
      alert('Failed to update contact. Please try again.');
    } finally {
      setUpdatingContact(false);
    }
  };

  const handleStageClick = (stageKey) => {
    if (selectedContact) {
      setCurrentStage(stageKey);
      setShowNoteModal(true);
    }
  };

  const handleUpdateStage = async () => {
    if (!selectedContact?.Email || !currentStage) return;

    const stageEndpoints = {
      lead: 'setlead',
      discover: 'discover',
      onboard: 'onboard',
      research: 'research',
      offer: 'offer',
      inspection: 'inspection',
      closing: 'closing',
      complete: 'complete'
    };

    const endpoint = stageEndpoints[currentStage];

    setUpdatingStage(true);
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/crm/${endpoint}?email=${encodeURIComponent(selectedContact.Email)}&status=${encodeURIComponent(selectedStatus)}&note=${encodeURIComponent(noteText)}&siteId=${SITE_CONFIG.SITE_ID}`, {
        method: 'POST',
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });

      if (response.ok) {
        fetchContactProgress(selectedContact.Email); // Refresh progress
        setShowNoteModal(false);
        setNoteText('');
        setCurrentStage('');
        setSelectedStatus('In-Progress');
      } else {
        alert('Failed to update stage. Please try again.');
      }
    } catch (error) {
      console.error('Error updating stage:', error);
      alert('Failed to update stage. Please try again.');
    } finally {
      setUpdatingStage(false);
    }
  };

  const handleAddDetails = async (inquiry) => {
    setCurrentSellingInquiry(inquiry);

    // Pre-populate form with available data
    setSellingDetailsForm({
      name: selectedContact?.Name || selectedContact?.name || inquiry.name || '',
      email: selectedContact?.Email || selectedContact?.email || inquiry.email || '',
      phoneNumber: selectedContact?.PhoneNumber || selectedContact?.phoneNumber || inquiry.phoneNumber || '',
      address: inquiry.address || inquiry.location || '',
      size: inquiry.propertyDetails || '',
      condition: '',
      timeline: '',
      listed: false,
      listingKey: '',
      status: ''
    });

    // Check if selling details already exist
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/selling-details?email=${encodeURIComponent(inquiry.email)}`, {
        headers: { 'X-API-Key': 'treas-api-key-2025' }
      });

      if (response.ok) {
        const existingDetails = await response.json();
        if (existingDetails && existingDetails.length > 0) {
          const details = existingDetails[0];
          setSellingDetailsForm(prev => ({
            ...prev,
            id: details.id,
            size: details.size || prev.size,
            condition: details.condition || prev.condition,
            timeline: details.timeline || prev.timeline,
            listed: details.listed || false,
            listingKey: details.listingKey || '',
            status: details.status || ''
          }));
          setIsUpdating(true);
        }
      }
    } catch (error) {
      console.error('Error checking existing details:', error);
    }

    setShowSellingDetailsModal(true);
  };

  const handleSubmitSellingDetails = async () => {
    setSubmittingDetails(true);
    try {
      let response;

      if (isUpdating) {
        // Update existing selling details
        const queryParams = new URLSearchParams({
          id: sellingDetailsForm.id,
          siteId: SITE_CONFIG.SITE_ID,
          listed: sellingDetailsForm.listed,
          listingKey: sellingDetailsForm.listingKey,
          status: sellingDetailsForm.status,
          address: sellingDetailsForm.address,
          condition: sellingDetailsForm.condition,
          timeline: sellingDetailsForm.timeline
        });

        response = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/update-selling-details?${queryParams}`, {
          method: 'POST',
          headers: { 'X-API-Key': 'treas-api-key-2025' }
        });
      } else {
        // Create new selling details
        const queryParams = new URLSearchParams({
          name: sellingDetailsForm.name,
          email: sellingDetailsForm.email,
          phoneNumber: sellingDetailsForm.phoneNumber,
          address: sellingDetailsForm.address,
          size: sellingDetailsForm.size,
          condition: sellingDetailsForm.condition,
          timeline: sellingDetailsForm.timeline,
          siteId: SITE_CONFIG.SITE_ID
        });

        response = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/selling-details?${queryParams}`, {
          method: 'POST',
          headers: { 'X-API-Key': 'treas-api-key-2025' }
        });
      }

      if (response.ok) {
        // Delete selling inquiry tiles
        const inquiriesToDelete = contactInterests.selling.filter(item =>
          item.isSellingInquiry && item.email === currentSellingInquiry.email
        );

        for (const inquiry of inquiriesToDelete) {
          await fetch(`https://tophamtechbe.com/api/treas/idx/v1/selling-inquiry?id=${inquiry.id}&siteId=${SITE_CONFIG.SITE_ID}`, {
            method: 'DELETE',
            headers: { 'X-API-Key': 'treas-api-key-2025' }
          });
        }

        setShowSellingDetailsModal(false);
        setIsUpdating(false);
        setCurrentSellingInquiry(null);

        // Refresh interests
        if (selectedContact?.Email || selectedContact?.email) {
          fetchContactInterests(selectedContact.Email || selectedContact.email);
        }
      } else {
        alert('Failed to save selling details');
      }
    } catch (error) {
      console.error('Error submitting selling details:', error);
      alert('Failed to save selling details');
    } finally {
      setSubmittingDetails(false);
    }
  };

  React.useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';

    fetchContacts();
    fetchChatLeads();
    fetchSiteUsers();
    fetchSenderConfig();

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- initial manager data load runs once per mount

  const completedJourneyStages = CLIENT_JOURNEY_STAGES.filter(
    (stage) => contactProgress?.[stage.key] === 'Completed'
  ).length;
  const journeyProgressPercent = (completedJourneyStages / CLIENT_JOURNEY_STAGES.length) * 100;
  const currentJourneyStage = CLIENT_JOURNEY_STAGES.find(
    stage => contactProgress?.[stage.key] === 'In-Progress'
  ) || CLIENT_JOURNEY_STAGES.find(stage => contactProgress?.[stage.key] !== 'Completed');
  const totalInterestSignals = (contactInterests?.buying?.length || 0)
    + (contactInterests?.selling?.length || 0)
    + (contactInterests?.chatLeads?.length || 0);
  const activePropertyAlerts = contactSubscriptions.filter(subscription => subscription.isActive).length;
  const dripEnrollments = Array.isArray(contactDripHistory?.enrollments) ? contactDripHistory.enrollments : [];
  const dripHistorySteps = dripEnrollments.flatMap(enrollment =>
    (Array.isArray(enrollment.steps) ? enrollment.steps : []).map(step => ({ ...step, enrollment }))
  );
  const scheduledDrips = dripHistorySteps.filter(step =>
    ['PENDING', 'SENDING', 'FAILED'].includes(String(step.status || '').toUpperCase())
  ).length;
  const lastSentDrip = [...dripHistorySteps]
    .filter(step => String(step.status || '').toUpperCase() === 'SENT')
    .sort((a, b) =>
      (historyDateValue(b.sentAt, b.scheduledAt, b.scheduledDate)?.getTime() || 0)
      - (historyDateValue(a.sentAt, a.scheduledAt, a.scheduledDate)?.getTime() || 0)
    )[0] || null;
  const nextScheduledDrip = [...dripHistorySteps]
    .filter(step => ['PENDING', 'SENDING', 'FAILED'].includes(String(step.status || '').toUpperCase()))
    .sort((a, b) =>
      (historyDateValue(a.nextAttemptAt, a.scheduledAt, a.scheduledDate)?.getTime() || Number.MAX_SAFE_INTEGER)
      - (historyDateValue(b.nextAttemptAt, b.scheduledAt, b.scheduledDate)?.getTime() || Number.MAX_SAFE_INTEGER)
    )[0] || null;
  const activeDripEnrollments = dripEnrollments.filter(enrollment =>
    String(enrollment.status || '').toUpperCase() === 'ACTIVE'
  ).length;
  const websiteSignals = (contactAnalytics?.summary?.pageViews || 0)
    + (contactAnalytics?.summary?.listingViews || 0)
    + (contactAnalytics?.summary?.searches || 0);
  const latestWebsiteActivity = contactAnalytics?.activity?.[0]?.occurredAt;

  const aiDripActiveRows = aiDripRows.filter(r => r.prompt.trim() || r.date || r.html);
  const aiDripGenerating = aiDripRows.some(r => r.generating);
  const aiDripReady = aiDripActiveRows.length > 0 && aiDripActiveRows.every(r => r.generated && r.generatedScope === aiDripScope && r.date);

  return (
    <div className={`client-manager ${isContactWorkspace ? `contact-workspace-page workspace-tab-${activeContactTab}` : ''}`}>
      <div className="manager-header">
        <h1>Client Manager</h1>
        <div className="header-buttons">
          <button onClick={() => navigate('/admin/client-manager/website-visitors')} className="website-visitors-button">
            Website Visitors
          </button>
          <button onClick={() => {
            setShowOpenHouseModal(true);
            fetchCurrentMlsKey();
          }} className="open-house-button">
            Set Open House
          </button>
          <button onClick={() => navigate('/admin')} className="back-button">
            Back to Admin
          </button>
        </div>
      </div>

      {isContactWorkspace && !selectedContact && (
        <div className="contact-workspace-state" role="status">
          <span className="contact-workspace-state-pulse" aria-hidden="true"></span>
          {loadingContacts ? (
            <>
              <h2>Opening client workspace</h2>
              <p>Bringing together contact details, engagement, marketing, and journey progress.</p>
            </>
          ) : (
            <>
              <h2>Client not found</h2>
              <p>This contact may have been removed or may not be available to your account.</p>
              <button type="button" onClick={closeContactWorkspace}>Return to Client Manager</button>
            </>
          )}
        </div>
      )}

      <div className="contacts-section-top">
        <div className="contacts-card-horizontal">
          <h3>Your Contacts ({contacts.length})</h3>
          <input
            type="text"
            placeholder="Search contacts..."
            value={contactSearch}
            onChange={(e) => setContactSearch(e.target.value)}
            className="search-input"
          />

          {loadingContacts ? (
            <div className="loading-contacts">Loading contacts...</div>
          ) : (
            <div className="contacts-list-horizontal">
              {contacts.length === 0 ? (
                <div className="empty-contacts">
                  <p>No contacts found</p>
                  <p>Upload a CSV file to add contacts</p>
                </div>
              ) : (
                contacts.filter(contact =>
                  contact.Name?.toLowerCase().includes(contactSearch.toLowerCase()) ||
                  contact.Email?.toLowerCase().includes(contactSearch.toLowerCase()) ||
                  contact.PhoneNumber?.toLowerCase().includes(contactSearch.toLowerCase()) ||
                  contact.Address1?.toLowerCase().includes(contactSearch.toLowerCase())
                ).sort((a, b) => {
                  if (!a.DateAdded && !b.DateAdded) return 0;
                  if (!a.DateAdded) return 1;
                  if (!b.DateAdded) return -1;
                  return new Date(b.DateAdded) - new Date(a.DateAdded);
                }).map((contact, index) => (
                  <div
                    key={index}
                    className={`contact-item-horizontal ${selectedContact?.Email === contact.Email ? 'selected' : ''}`}
                    onClick={() => handleContactClick(contact)}
                  >
                    <div className="contact-name">
                      {contact.Name}
                      {contact.Group && <span className="contact-group-badge">{contact.Group}</span>}
                    </div>
                    <div className="contact-address-horizontal">
                      {contact.Address1}
                      {contact.Address2 && `, ${contact.Address2}`}
                      <br />
                      {contact.City}, {contact.State} {contact.Zip}
                    </div>
                    <div className="contact-info-horizontal">
                      {contact.Email && <div>📧 {contact.Email}</div>}
                      {contact.PhoneNumber && <div>📞 {contact.PhoneNumber}</div>}
                      {contact.CompanyName && <div>🏢 {contact.CompanyName}</div>}
                      {contact.DateAdded && <div>📅 {contact.DateAdded}</div>}
                      {contact.interest_area && <div>📍 {contact.interest_area}</div>}
                      {contact.price_target && <div>💰 {contact.price_target}</div>}
                      {contact.assigned_agent && <div>🧑‍💼 {contact.assigned_agent}</div>}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      <div className="contact-progress-section">
        {selectedContact && selectedContact.Email && (
          <div className="contact-detail-overlay">
            <div className="contact-detail-modal-bar">
              <div className="contact-workspace-command-bar">
                <button className="contact-workspace-back" type="button" onClick={closeContactWorkspace}>
                  <WorkspaceIcon name="back" />
                  <span>All clients</span>
                </button>
                <span className="contact-workspace-breadcrumb">Client workspace</span>
                <div className="contact-workspace-actions">
                  {selectedContact.Email && (
                    <a href={`mailto:${selectedContact.Email}`} title={`Email ${selectedContact.Name}`}>
                      <WorkspaceIcon name="mail" />
                      <span>Email</span>
                    </a>
                  )}
                  {selectedContact.PhoneNumber && (
                    <a href={`tel:${selectedContact.PhoneNumber}`} title={`Call ${selectedContact.Name}`}>
                      <WorkspaceIcon name="phone" />
                      <span>Call</span>
                    </a>
                  )}
                  <button type="button" onClick={(e) => handleEditClick(e, selectedContact)} title="Edit contact">
                    <WorkspaceIcon name="edit" />
                    <span>Edit</span>
                  </button>
                  <button className="danger" type="button" onClick={(e) => handleDeleteClick(e, selectedContact)} title="Delete contact">
                    <WorkspaceIcon name="trash" />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
              <div className="contact-detail-avatar">
                {(selectedContact.Name || '?')[0].toUpperCase()}
              </div>
              <div className="contact-detail-main">
                <div className="contact-detail-name-row">
                  <h2>{selectedContact.Name}</h2>
                  {selectedContact.Group && <span className="contact-detail-badge">{selectedContact.Group}</span>}
                </div>
                <div className="contact-detail-meta">
                  {selectedContact.Email && <span>📧 {selectedContact.Email}</span>}
                  {selectedContact.PhoneNumber && <span>📞 {selectedContact.PhoneNumber}</span>}
                  {selectedContact.Address1 && <span>🏠 {selectedContact.Address1}{selectedContact.City ? `, ${selectedContact.City}` : ''} {selectedContact.State} {selectedContact.Zip}</span>}
                  {selectedContact.interest_area && <span>📍 {selectedContact.interest_area}</span>}
                  {selectedContact.price_target && <span>💰 ${selectedContact.price_target}</span>}
                  {selectedContact.assigned_agent && <span>🧑‍💼 {selectedContact.assigned_agent}</span>}
                  {selectedContact.CompanyName && <span>🏢 {selectedContact.CompanyName}</span>}
                  {selectedContact.DateAdded && <span>📅 {selectedContact.DateAdded}</span>}
                </div>
                {contactInterests && (
                  <div className="contact-detail-stats">
                    {contactInterests.buying?.length > 0 && <span className="stat-pill buying">🏠 {contactInterests.buying.length} liked</span>}
                    {contactInterests.selling?.length > 0 && <span className="stat-pill selling">📋 {contactInterests.selling.length} selling</span>}
                    {contactInterests.chatLeads?.length > 0 && <span className="stat-pill leads">💬 {contactInterests.chatLeads.length} leads</span>}
                  </div>
                )}
                <nav className="contact-workspace-tabs" aria-label="Client workspace sections">
                  {CONTACT_WORKSPACE_TABS.map(tab => (
                    <button
                      type="button"
                      key={tab.key}
                      className={activeContactTab === tab.key ? 'active' : ''}
                      onClick={() => setActiveContactTab(tab.key)}
                      aria-current={activeContactTab === tab.key ? 'page' : undefined}
                    >
                      <WorkspaceIcon name={tab.key} />
                      <span>{tab.label}</span>
                    </button>
                  ))}
                </nav>

                <section className="contact-workspace-overview" aria-label="Client overview">
                  <div className="contact-overview-intro">
                    <span className="contact-overview-kicker">At a glance</span>
                    <h3>A clear view of this relationship</h3>
                    <p>Signals, automations, and next steps—organized around what needs attention now.</p>
                  </div>
                  <div className="contact-overview-metrics">
                    <button type="button" onClick={() => setActiveContactTab('journey')} className="contact-overview-card journey">
                      <span className="contact-overview-card-icon"><WorkspaceIcon name="journey" /></span>
                      <span className="contact-overview-card-copy">
                        <small>Journey progress</small>
                        <strong>{completedJourneyStages} of {CLIENT_JOURNEY_STAGES.length}</strong>
                        <span>{currentJourneyStage ? `Next: ${currentJourneyStage.label}` : 'Relationship complete'}</span>
                      </span>
                      <span className="contact-overview-ring" style={{ '--progress': `${journeyProgressPercent * 3.6}deg` }}>
                        <b>{Math.round(journeyProgressPercent)}%</b>
                      </span>
                    </button>
                    <button type="button" onClick={() => setActiveContactTab('interests')} className="contact-overview-card interests">
                      <span className="contact-overview-card-icon"><WorkspaceIcon name="interests" /></span>
                      <span className="contact-overview-card-copy">
                        <small>Intent signals</small>
                        <strong>{totalInterestSignals}</strong>
                        <span>{contactInterests?.buying?.length || 0} liked · {contactInterests?.selling?.length || 0} selling</span>
                      </span>
                    </button>
                    <button type="button" onClick={() => setActiveContactTab('marketing')} className="contact-overview-card marketing">
                      <span className="contact-overview-card-icon"><WorkspaceIcon name="marketing" /></span>
                      <span className="contact-overview-card-copy">
                        <small>Active marketing</small>
                        <strong>{activePropertyAlerts + scheduledDrips}</strong>
                        <span>{activePropertyAlerts} alerts · {scheduledDrips} scheduled emails</span>
                      </span>
                    </button>
                    <button type="button" onClick={() => setActiveContactTab('activity')} className="contact-overview-card activity">
                      <span className="contact-overview-card-icon"><WorkspaceIcon name="activity" /></span>
                      <span className="contact-overview-card-copy">
                        <small>Website signals</small>
                        <strong>{websiteSignals}</strong>
                        <span>{latestWebsiteActivity ? `Last seen ${new Date(latestWebsiteActivity).toLocaleDateString()}` : 'No recent linked activity'}</span>
                      </span>
                    </button>
                  </div>
                  <div className="contact-overview-focus">
                    <div>
                      <span className="contact-overview-kicker">Relationship focus</span>
                      <strong>{selectedContact.interest_area || 'Discover preferred areas'}</strong>
                      <p>{selectedContact.price_target ? `Target range ${selectedContact.price_target}` : 'Add a target price to sharpen recommendations.'}</p>
                    </div>
                    <div>
                      <span className="contact-overview-kicker">Ownership</span>
                      <strong>{selectedContact.assigned_agent || 'Unassigned'}</strong>
                      <p>{selectedContact.DateAdded ? `Contact since ${selectedContact.DateAdded}` : 'Contact date unavailable'}</p>
                    </div>
                  </div>
                </section>
                <div className="contact-detail-columns">
                  <div className="contact-detail-col-left workspace-panel-interests">
                    {contactInterests && (
                      <>
                        {contactInterests.buying?.length > 0 && (
                          <div className="detail-interest-group">
                            <h4>Liked Properties</h4>
                            <div className="detail-interest-items">
                              {contactInterests.buying.map((item, i) => (
                                <div key={i} className="detail-interest-item property-interest-item">
                                  <a href={`/listing/${item.listingKey}`}>{item.listingKey}</a>
                                  {item.area && <span>{item.area}</span>}
                                  {item.price && <span>${Number(item.price).toLocaleString()}</span>}
                                  <span>{new Date(item.datetime).toLocaleDateString()}</span>
                                  {renderShowingControls(item)}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {contactInterests.selling?.length > 0 && (
                          <div className="detail-interest-group">
                            <h4>Selling</h4>
                            <div className="detail-interest-items">
                              {contactInterests.selling.map((item, i) => (
                                <div key={i} className="detail-interest-item">
                                  <span>{item.address || item.location || 'Property'}</span>
                                  {item.timeline && <span>{item.timeline}</span>}
                                  <span>{new Date(item.datetime || item.created_at).toLocaleDateString()}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {contactInterests.chatLeads?.length > 0 && (
                          <div className="detail-interest-group">
                            <h4>Chat Leads</h4>
                            <div className="detail-interest-items">
                              {contactInterests.chatLeads.map((item, i) => (
                                <div key={i} className="detail-interest-item">
                                  <span>{item.serviceReq}</span>
                                  {item.area && <span>{item.area}</span>}
                                  {item.listingKey && <a href={`/listing/${item.listingKey}`}>{item.listingKey}</a>}
                                  <span>{new Date(item.createdAt).toLocaleDateString()}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {totalInterestSignals === 0 && (
                          <div className="contact-workspace-empty">
                            <span><WorkspaceIcon name="interests" /></span>
                            <strong>No intent signals yet</strong>
                            <p>Liked properties, seller inquiries, and chat leads will appear here.</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <div className="contact-detail-col-right workspace-panel-alerts">
                <div className="sub-header">
                  <h4>Property Alerts</h4>
                  <button className="sub-add-btn" onClick={() => { setEditingSub(null); setSubForm(emptySubForm()); setShowSubForm(true); }}>+ New Alert</button>
                </div>
                {loadingSubscriptions ? <p style={{color:'#94a3b8',fontSize:'12px'}}>Loading...</p> : contactSubscriptions.length > 0 ? (
                  <div className="sub-list">
                    {[...contactSubscriptions].sort((a, b) => b.isActive - a.isActive).map(sub => {
                      const filters = typeof sub.searchFilters === 'string' ? JSON.parse(sub.searchFilters) : (sub.searchFilters || {});
                      return (
                        <div key={sub.id} className={`sub-item ${!sub.isActive ? 'inactive' : ''}`}>
                          <div className="sub-item-info">
                            <span className="sub-filters">{Object.entries(filters).map(([k, v]) => `${k}: ${v}`).join(', ')}</span>
                            <span className="sub-cadence">📬 {sub.cadence} • {sub.isActive ? '✅ active' : '⏸ inactive'}</span>
                          </div>
                          <div className="sub-item-actions">
                            <button onClick={() => { setEditingSub(sub); setSubForm({ ...emptySubForm(), cadence: sub.cadence, city: filters.city || '', state: filters.state || '', zip: filters.zip || '', county: filters.county || '', minPrice: filters.minPrice || '', maxPrice: filters.maxPrice || '', minBedrooms: filters.minBedrooms || '', maxBedrooms: filters.maxBedrooms || '', minBathrooms: filters.minBathrooms || '', maxBathrooms: filters.maxBathrooms || '', minSqft: filters.minSqft || '', maxSqft: filters.maxSqft || '', propertyType: filters.propertyType || '', propertySubType: filters.propertySubType || '', stories: filters.stories || '', minYearBuilt: filters.minYearBuilt || '', maxYearBuilt: filters.maxYearBuilt || '', acreage: filters.maxLotSizeAcres === 5 ? '<5' : filters.minLotSizeAcres === 5 && filters.maxLotSizeAcres === 10 ? '5to10' : filters.minLotSizeAcres === 10 ? '10+' : '', foundation: filters.foundation || '', schoolDistrict: filters.schoolDistrict || '', hasMedia: !!filters.hasMedia, hasFence: !!filters.hasFence, hasPool: !!filters.hasPool, communityFeatures: Array.isArray(filters.communityFeatures) ? filters.communityFeatures : [] }); setShowSubForm(true); }}>✏️</button>
                            {sub.isActive && <button onClick={async () => { await fetch(`https://tophamtechbe.com/api/treas/comm/property-search-subscriptions/${sub.id}`, { method: 'DELETE', headers: { 'X-API-Key': 'treas-api-key-2025' } }); fetchContactSubscriptions(selectedContact.Email || selectedContact.email); }}>⏸</button>}
                          </div>
                          {subSendLogs.filter(l => l.subscriptionId === sub.id).length > 0 && (
                            <div className="sub-send-logs">
                              {subSendLogs.filter(l => l.subscriptionId === sub.id).slice(0, 3).map(log => (
                                <div key={log.id} className="sub-log-item">
                                  <span className={`sub-log-status ${log.status}`}>{log.status === 'sent' ? '✅' : '⏭️'}</span>
                                  <span>{log.scheduledDate}</span>
                                  <span>{log.resultCount} listings</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : <p style={{color:'#94a3b8',fontSize:'12px'}}>No alerts set up</p>}
                {showSubForm && (
                  <div className="sub-modal-overlay" onClick={() => { setShowSubForm(false); setSubFormSuccess(false); }}>
                    <div className="sub-modal-popup" onClick={e => e.stopPropagation()}>
                      {subFormSuccess ? (
                        <div className="sub-modal-success">
                          <div className="sub-modal-success-icon">✅</div>
                          <h3>{editingSub ? 'Alert Updated!' : 'Alert Created!'}</h3>
                          <p>Property alert is now {editingSub ? 'updated' : 'active'}.</p>
                        </div>
                      ) : (
                        <>
                          <h3>{editingSub ? 'Edit Property Alert' : 'New Property Alert'}</h3>
                          <p className="sub-modal-desc">Set up automated listing alerts for this contact.</p>
                          <div className="sub-modal-fields">
                            <div className="sub-modal-row">
                              <label>City</label>
                              <input placeholder="e.g. Delray Beach" value={subForm.city} onChange={e => setSubForm({...subForm, city: e.target.value})} />
                            </div>
                            <div className="sub-modal-row">
                              <label>State</label>
                              <input placeholder="e.g. FL" value={subForm.state} onChange={e => setSubForm({...subForm, state: e.target.value})} maxLength="2" />
                            </div>
                            <div className="sub-modal-row">
                              <label>Zip</label>
                              <input placeholder="e.g. 33483" value={subForm.zip} onChange={e => setSubForm({...subForm, zip: e.target.value})} />
                            </div>
                            <div className="sub-modal-row">
                              <label>County</label>
                              <input placeholder="e.g. Palm Beach" value={subForm.county} onChange={e => setSubForm({...subForm, county: e.target.value})} />
                            </div>
                            <div className="sub-modal-row">
                              <label>Min Price</label>
                              <input placeholder="200000" type="number" value={subForm.minPrice} onChange={e => setSubForm({...subForm, minPrice: e.target.value})} />
                            </div>
                            <div className="sub-modal-row">
                              <label>Max Price</label>
                              <input placeholder="400000" type="number" value={subForm.maxPrice} onChange={e => setSubForm({...subForm, maxPrice: e.target.value})} />
                            </div>
                            <div className="sub-modal-row">
                              <label>Min Bedrooms</label>
                              <input placeholder="3" type="number" value={subForm.minBedrooms} onChange={e => setSubForm({...subForm, minBedrooms: e.target.value})} />
                            </div>
                            <div className="sub-modal-row">
                              <label>Max Bedrooms</label>
                              <input placeholder="5" type="number" value={subForm.maxBedrooms} onChange={e => setSubForm({...subForm, maxBedrooms: e.target.value})} />
                            </div>
                            <div className="sub-modal-row">
                              <label>Min Bathrooms</label>
                              <input placeholder="2" type="number" value={subForm.minBathrooms} onChange={e => setSubForm({...subForm, minBathrooms: e.target.value})} />
                            </div>
                            <div className="sub-modal-row">
                              <label>Max Bathrooms</label>
                              <input placeholder="4" type="number" value={subForm.maxBathrooms} onChange={e => setSubForm({...subForm, maxBathrooms: e.target.value})} />
                            </div>
                            <div className="sub-modal-row">
                              <label>Min Sqft</label>
                              <input placeholder="1500" type="number" value={subForm.minSqft} onChange={e => setSubForm({...subForm, minSqft: e.target.value})} />
                            </div>
                            <div className="sub-modal-row">
                              <label>Max Sqft</label>
                              <input placeholder="3000" type="number" value={subForm.maxSqft} onChange={e => setSubForm({...subForm, maxSqft: e.target.value})} />
                            </div>
                            <div className="sub-modal-row">
                              <label>Stories</label>
                              <input placeholder="2" type="number" value={subForm.stories} onChange={e => setSubForm({...subForm, stories: e.target.value})} />
                            </div>
                            <div className="sub-modal-row">
                              <label>Min Year Built</label>
                              <input placeholder="1990" type="number" value={subForm.minYearBuilt} onChange={e => setSubForm({...subForm, minYearBuilt: e.target.value})} />
                            </div>
                            <div className="sub-modal-row">
                              <label>Max Year Built</label>
                              <input placeholder="2020" type="number" value={subForm.maxYearBuilt} onChange={e => setSubForm({...subForm, maxYearBuilt: e.target.value})} />
                            </div>
                            <div className="sub-modal-row">
                              <label>Foundation</label>
                              <select value={subForm.foundation} onChange={e => setSubForm({...subForm, foundation: e.target.value})}>
                                <option value="">Any</option>
                                <option value="CrawlSpace">Crawl Space</option>
                                <option value="Slab">Slab</option>
                              </select>
                            </div>
                            <div className="sub-modal-row">
                              <label>School District</label>
                              <input placeholder="e.g. Jenks" value={subForm.schoolDistrict} onChange={e => setSubForm({...subForm, schoolDistrict: e.target.value})} />
                            </div>
                            <div className="sub-modal-row">
                              <label>Acreage</label>
                              <select value={subForm.acreage} onChange={e => setSubForm({...subForm, acreage: e.target.value})}>
                                <option value="">Any</option>
                                <option value="<5">&lt; 5 Acres</option>
                                <option value="5to10">5 to 10 Acres</option>
                                <option value="10+">10+ Acres</option>
                              </select>
                            </div>
                            <div className="sub-modal-row">
                              <label>Property Type</label>
                              <select value={subForm.propertyType} onChange={e => setSubForm({...subForm, propertyType: e.target.value})}>
                                <option value="">Any</option>
                                <option value="Residential">Residential</option>
                                <option value="ResidentialLease">Residential Lease</option>
                                <option value="Land">Land</option>
                                <option value="Farm">Farm</option>
                              </select>
                            </div>
                            <div className="sub-modal-row">
                              <label>Sub Type</label>
                              <select value={subForm.propertySubType} onChange={e => setSubForm({...subForm, propertySubType: e.target.value})}>
                                <option value="">Any</option>
                                <option value="SingleFamilyResidence">Single Family</option>
                                <option value="Condominium">Condominium</option>
                                <option value="Townhouse">Townhouse</option>
                              </select>
                            </div>
                            <div className="sub-modal-row">
                              <label>Cadence</label>
                              <select value={subForm.cadence} onChange={e => setSubForm({...subForm, cadence: e.target.value})}>
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                              </select>
                            </div>
                            <div className="sub-modal-row">
                              <label>Community Features</label>
                              <select multiple value={subForm.communityFeatures} onChange={e => setSubForm({...subForm, communityFeatures: Array.from(e.target.selectedOptions, o => o.value)})}>
                                <option value="greenbelt">Greenbelt</option>
                                <option value="hill">Hill Country</option>
                                <option value="golf">Golf Course</option>
                                <option value="downtown">Downtown</option>
                                <option value="water">Water</option>
                              </select>
                            </div>
                          </div>
                          <div className="sub-modal-checks">
                            <label><input type="checkbox" checked={subForm.hasMedia} onChange={e => setSubForm({...subForm, hasMedia: e.target.checked})} /> Photos only</label>
                            <label><input type="checkbox" checked={subForm.hasFence} onChange={e => setSubForm({...subForm, hasFence: e.target.checked})} /> Fenced yard</label>
                            <label><input type="checkbox" checked={subForm.hasPool} onChange={e => setSubForm({...subForm, hasPool: e.target.checked})} /> Pool</label>
                          </div>
                          <div className="sub-modal-actions">
                            <button className="sub-modal-cancel" onClick={() => setShowSubForm(false)}>Cancel</button>
                            <button className="sub-modal-save" onClick={async () => {
                              const filters = {};
                              if (subForm.city) filters.city = subForm.city;
                              if (subForm.state) filters.state = subForm.state;
                              if (subForm.zip) filters.zip = subForm.zip;
                              if (subForm.county) filters.county = subForm.county;
                              if (subForm.minPrice) filters.minPrice = Number(subForm.minPrice);
                              if (subForm.maxPrice) filters.maxPrice = Number(subForm.maxPrice);
                              if (subForm.minBedrooms) filters.minBedrooms = Number(subForm.minBedrooms);
                              if (subForm.maxBedrooms) filters.maxBedrooms = Number(subForm.maxBedrooms);
                              if (subForm.minBathrooms) filters.minBathrooms = Number(subForm.minBathrooms);
                              if (subForm.maxBathrooms) filters.maxBathrooms = Number(subForm.maxBathrooms);
                              if (subForm.minSqft) filters.minSqft = Number(subForm.minSqft);
                              if (subForm.maxSqft) filters.maxSqft = Number(subForm.maxSqft);
                              if (subForm.propertyType) filters.propertyType = subForm.propertyType;
                              if (subForm.propertySubType) filters.propertySubType = subForm.propertySubType;
                              if (subForm.stories) filters.stories = Number(subForm.stories);
                              if (subForm.minYearBuilt) filters.minYearBuilt = Number(subForm.minYearBuilt);
                              if (subForm.maxYearBuilt) filters.maxYearBuilt = Number(subForm.maxYearBuilt);
                              if (subForm.acreage === '<5') filters.maxLotSizeAcres = 5;
                              else if (subForm.acreage === '5to10') { filters.minLotSizeAcres = 5; filters.maxLotSizeAcres = 10; }
                              else if (subForm.acreage === '10+') filters.minLotSizeAcres = 10;
                              if (subForm.foundation) filters.foundation = subForm.foundation;
                              if (subForm.schoolDistrict) filters.schoolDistrict = subForm.schoolDistrict;
                              if (subForm.hasMedia) filters.hasMedia = true;
                              if (subForm.hasFence) filters.hasFence = true;
                              if (subForm.hasPool) filters.hasPool = true;
                              if (subForm.communityFeatures.length > 0) filters.communityFeatures = subForm.communityFeatures;
                              const email = selectedContact.Email || selectedContact.email;
                              if (editingSub) {
                                await fetch(`https://tophamtechbe.com/api/treas/comm/property-search-subscriptions/${editingSub.id}`, {
                                  method: 'PUT',
                                  headers: { 'Content-Type': 'application/json', 'X-API-Key': 'treas-api-key-2025' },
                                  body: JSON.stringify({ cadence: subForm.cadence, searchFilters: filters })
                                });
                              } else {
                                await fetch('https://tophamtechbe.com/api/treas/comm/property-search-subscriptions', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', 'X-API-Key': 'treas-api-key-2025' },
                                  body: JSON.stringify({ customerName: selectedContact.Name || selectedContact.name || '', agentName: SITE_CONFIG.AGENT.NAME, emailAddress: email, siteId: SITE_CONFIG.SITE_ID, cadence: subForm.cadence, listingBaseUrl: `https://www.${SITE_CONFIG.BASE_URL}`, searchFilters: filters })
                                });
                              }
                              setSubFormSuccess(true);
                              fetchContactSubscriptions(email);
                              setTimeout(() => { setShowSubForm(false); setSubFormSuccess(false); }, 2000);
                            }}>{editingSub ? 'Update Alert' : 'Create Alert'}</button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
                  </div>
                  <div className="contact-detail-col-right workspace-panel-drips">
                    <div className="sub-drips-section">
                      <div className="sub-header">
                        <h4>Personal Drip Emails</h4>
                        <button className="sub-add-btn" onClick={openAiDripModal}>✨ Personal Drip</button>
                      </div>
                      {(contactDripStatus === 'idle' || contactDripStatus === 'loading') && (
                        <div className="drip-history-state">Loading secure email history…</div>
                      )}
                      {contactDripStatus === 'error' && (
                        <div className="drip-history-state error" role="alert">
                          <strong>{contactDripError?.status === 403 ? 'History access denied' : contactDripError?.status === 503 ? 'Authentication temporarily unavailable' : 'Email history is unavailable'}</strong>
                          <p>{contactDripError?.message}</p>
                          {contactDripError?.status !== 403 && <button type="button" onClick={() => fetchContactSubscriptions(selectedContact.Email || selectedContact.email)}>Try again</button>}
                        </div>
                      )}
                      {contactDripStatus === 'empty' && (
                        <div className="contact-workspace-empty compact">
                          <span><WorkspaceIcon name="marketing" /></span>
                          <strong>No drip email history</strong>
                          <p>Create a personalized sequence when this relationship needs a thoughtful follow-up.</p>
                        </div>
                      )}
                      {contactDripStatus === 'ready' && (
                        <div className="drip-history">
                          <div className="drip-history-summary">
                            <div><small>Campaign status</small><strong>{activeDripEnrollments ? `${activeDripEnrollments} active` : 'No active campaign'}</strong><span>{dripEnrollments.length} enrollment{dripEnrollments.length === 1 ? '' : 's'} on record</span></div>
                            <div><small>Last sent</small><strong>{lastSentDrip ? dripStepName(lastSentDrip) : 'Nothing sent yet'}</strong><span>{lastSentDrip ? formatHistoryDate(lastSentDrip.sentAt, lastSentDrip.scheduledAt, lastSentDrip.scheduledDate) : 'No completed delivery'}</span>{lastSentDrip?.enrollment?.legacy && !lastSentDrip.sentAt && <em>Legacy record · exact sent time unavailable</em>}</div>
                            <div><small>Next scheduled</small><strong>{nextScheduledDrip ? dripStepName(nextScheduledDrip) : 'Nothing scheduled'}</strong><span>{nextScheduledDrip ? formatHistoryDate(nextScheduledDrip.nextAttemptAt, nextScheduledDrip.scheduledAt, nextScheduledDrip.scheduledDate) : 'No pending delivery'}</span></div>
                          </div>
                          <p className="drip-history-note">Template details labeled <strong>Current template</strong> reflect today’s template configuration, not a historical snapshot.</p>
                          <div className="drip-history-enrollments">
                            {dripEnrollments.map((enrollment, enrollmentIndex) => (
                              <article className="drip-history-enrollment" key={enrollment.enrollmentId || `${enrollment.campaignKey}-${enrollmentIndex}`}>
                                <header>
                                  <div><small>Campaign {enrollment.campaignVersion ? `· Version ${enrollment.campaignVersion}` : ''}{enrollment.legacy ? ' · Legacy' : ''}</small><h5>{formatCampaignName(enrollment.campaignKey)}</h5><p>Enrolled {formatHistoryDate(enrollment.enrolledAt)}{enrollment.completedAt ? ` · Completed ${formatHistoryDate(enrollment.completedAt)}` : ''}</p></div>
                                  <span className={`drip-history-status ${dripStatusTone(enrollment.status)}`}>{dripStatusLabel(enrollment.status)}</span>
                                </header>
                                {Array.isArray(enrollment.steps) && enrollment.steps.length > 0 ? (
                                  <div className="drip-history-steps">
                                    {[...enrollment.steps].sort((a, b) => Number(a.stepNumber || 0) - Number(b.stepNumber || 0)).map((step, stepIndex) => (
                                      <div className="drip-history-step" key={step.messageId || `${step.stepNumber}-${stepIndex}`}>
                                        <span className="drip-history-number">{step.stepNumber || stepIndex + 1}</span>
                                        <div className="drip-history-step-copy">
                                          <div className="drip-history-step-title"><strong>{dripStepName(step)}</strong>{step.templateSource === 'CURRENT_TEMPLATE' && <span>Current template</span>}</div>
                                          {step.subject && step.templateName && <p>{step.templateName}</p>}
                                          <div className="drip-history-dates">
                                            <span>Scheduled <b>{formatHistoryDate(step.scheduledAt, step.scheduledDate)}</b></span>
                                            {String(step.status || '').toUpperCase() === 'SENT' && <span>Sent <b>{step.sentAt ? formatHistoryDate(step.sentAt) : enrollment.legacy ? 'Legacy time unavailable' : 'Time unavailable'}</b></span>}
                                            {Number(step.attemptCount) > 0 && <span>{step.attemptCount} delivery attempt{Number(step.attemptCount) === 1 ? '' : 's'}</span>}
                                            {step.nextAttemptAt && <span>Next retry <b>{formatHistoryDate(step.nextAttemptAt)}</b></span>}
                                          </div>
                                          {step.error && <p className="drip-history-error-detail">{step.error}</p>}
                                        </div>
                                        <span className={`drip-history-status ${dripStatusTone(step.status)}`}>{dripStatusLabel(step.status)}</span>
                                      </div>
                                    ))}
                                  </div>
                                ) : <div className="drip-history-no-steps">No email steps are recorded for this enrollment.</div>}
                              </article>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    {showAiDripModal && (
                      <div className="sub-modal-overlay" onClick={() => setShowAiDripModal(false)}>
                        <div className="sub-modal-popup ai-drip-popup" onClick={e => e.stopPropagation()}>
                          {aiDripSuccess ? (
                            <div className="sub-modal-success">
                              <div className="sub-modal-success-icon">✅</div>
                              <h3>AI Drip Scheduled!</h3>
                              <p>The campaign is now on the calendar.</p>
                            </div>
                          ) : (
                            <>
                              <h3>✨ Personal Drip Campaign</h3>
                              <p className="sub-modal-desc">Generate up to 3 emails for {selectedContact.Name} only and choose their calendar send dates.</p>

                              <div className="ai-drip-scope" role="radiogroup" aria-label="Drip campaign signature and access">
                                <label className={`ai-drip-scope-option ${aiDripScope === 'agent' ? 'selected' : ''} ${!currentUserId || aiDripGenerating ? 'disabled' : ''}`}>
                                  <input
                                    type="radio"
                                    name="ai-drip-scope"
                                    checked={aiDripScope === 'agent'}
                                    disabled={!currentUserId || aiDripGenerating}
                                    onChange={() => handleAiDripScopeChange('agent')}
                                  />
                                  <span><strong>Personal</strong><small>Use your agent profile and signature.</small></span>
                                </label>
                                <label className={`ai-drip-scope-option ${aiDripScope === 'brokerage' ? 'selected' : ''} ${aiDripGenerating ? 'disabled' : ''}`}>
                                  <input
                                    type="radio"
                                    name="ai-drip-scope"
                                    checked={aiDripScope === 'brokerage'}
                                    disabled={aiDripGenerating}
                                    onChange={() => handleAiDripScopeChange('brokerage')}
                                  />
                                  <span><strong>Brokerage-wide</strong><small>Use the brokerage signature.</small></span>
                                </label>
                              </div>

                              <div className="ai-drip-cadence">
                                <label className="ai-drip-cadence-option">
                                  <input
                                    type="checkbox"
                                    checked={aiDripCadence === 'weekly'}
                                    onChange={() => handleAiDripCadenceToggle('weekly')}
                                  />
                                  Send weekly
                                </label>
                                <label className="ai-drip-cadence-option">
                                  <input
                                    type="checkbox"
                                    checked={aiDripCadence === 'monthly'}
                                    onChange={() => handleAiDripCadenceToggle('monthly')}
                                  />
                                  Send monthly
                                </label>
                                {aiDripCadence && (
                                  <label className="ai-drip-start">
                                    Start
                                    <input
                                      type="date"
                                      value={aiDripStartDate}
                                      onChange={e => { setAiDripStartDate(e.target.value); applyAiDripCadence(aiDripCadence, e.target.value); }}
                                    />
                                  </label>
                                )}
                              </div>

                              <div className="ai-drip-rows">
                                {aiDripRows.map((row, i) => (
                                  <React.Fragment key={i}>
                                  <div className="ai-drip-row">
                                    <div className="ai-drip-row-number">Email {i + 1}</div>
                                    <input
                                      type="text"
                                      placeholder="Describe the email... e.g. spring market update"
                                      value={row.prompt}
                                      onChange={e => {
                                        updateAiDripRow(i, { prompt: e.target.value, generated: false, error: '' });
                                        if (aiDripPreviewIndex === i) setAiDripPreviewIndex(null);
                                      }}
                                      className="ai-drip-prompt"
                                    />
                                    <input
                                      type="date"
                                      value={row.date}
                                      onChange={e => updateAiDripRow(i, { date: e.target.value })}
                                      className="ai-drip-date"
                                    />
                                    <div className="ai-drip-row-actions">
                                      <button
                                        type="button"
                                        onClick={() => generateAiDripEmail(i)}
                                        disabled={row.generating || !row.prompt.trim()}
                                        className="ai-drip-generate-btn"
                                      >
                                        {row.generating ? '⏳ Generating...' : row.generated ? '✅ Generated' : '✨ Generate'}
                                      </button>
                                      {row.generated && (
                                        <button
                                          type="button"
                                          onClick={() => setAiDripPreviewIndex(aiDripPreviewIndex === i ? null : i)}
                                          className="ai-drip-preview-btn"
                                          title={aiDripPreviewIndex === i ? 'Close preview' : 'Preview email'}
                                        >
                                          {aiDripPreviewIndex === i ? '✕' : '👁'}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  {aiDripPreviewIndex === i && row.html && (
                                    <div className="ai-drip-preview">
                                      <div className="ai-drip-preview-header">
                                        <span>📧 {row.subject || 'Email preview'}</span>
                                        <button type="button" onClick={() => setAiDripPreviewIndex(null)}>×</button>
                                      </div>
                                      <iframe
                                        srcDoc={row.html}
                                        className="ai-drip-preview-frame"
                                        title={`Email ${i + 1} preview`}
                                      />
                                    </div>
                                  )}
                                  </React.Fragment>
                                ))}
                              </div>

                              {(aiDripRows.some(r => r.error) || aiDripError) && (
                                <div className="ai-drip-error">
                                  {aiDripRows.find(r => r.error)?.error || aiDripError}
                                </div>
                              )}

                              <div className="sub-modal-actions">
                                <button className="sub-modal-cancel" onClick={() => setShowAiDripModal(false)}>Cancel</button>
                                <button className="sub-modal-save" onClick={scheduleAiDrip} disabled={schedulingAiDrip || !aiDripReady}>
                                  {schedulingAiDrip ? 'Scheduling...' : 'Schedule Drip'}
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <section className="contact-analytics-panel workspace-panel-activity" aria-labelledby="contact-analytics-title">
                  <div className="contact-analytics-header">
                    <div>
                      <h4 id="contact-analytics-title">Website Activity</h4>
                      <p>Pages, property views, and searches connected to this contact.</p>
                    </div>
                    <div className="contact-analytics-ranges" aria-label="Website activity date range">
                      {CONTACT_ANALYTICS_RANGES.map(range => (
                        <button
                          type="button"
                          key={range}
                          className={contactAnalyticsRange === range ? 'active' : ''}
                          onClick={() => setContactAnalyticsRange(range)}
                        >
                          {range}
                        </button>
                      ))}
                    </div>
                  </div>

                  {contactAnalyticsStatus === 'loading' && (
                    <p className="contact-analytics-message">Loading website activity...</p>
                  )}
                  {contactAnalyticsStatus === 'unregistered' && (
                    <p className="contact-analytics-message">
                      No consented analytics link is available for this contact yet.
                    </p>
                  )}
                  {contactAnalyticsStatus === 'error' && (
                    <p className="contact-analytics-message error">{contactAnalyticsError}</p>
                  )}
                  {contactAnalyticsStatus === 'ready' && !contactAnalytics?.configured && (
                    <p className="contact-analytics-message">Analytics are not configured for this site.</p>
                  )}
                  {contactAnalyticsStatus === 'ready' && contactAnalytics?.configured && !contactAnalytics?.linked && (
                    <p className="contact-analytics-message">
                      No active analytics link is available. The user may not have consented, may have opted out, or the link may have expired.
                    </p>
                  )}
                  {contactAnalyticsStatus === 'ready' && contactAnalytics?.linked && (
                    <>
                      <div className="contact-analytics-summary">
                        <span><strong>{contactAnalytics.summary?.pageViews || 0}</strong> page views</span>
                        <span><strong>{contactAnalytics.summary?.listingViews || 0}</strong> properties</span>
                        <span><strong>{contactAnalytics.summary?.searches || 0}</strong> searches</span>
                      </div>
                      {contactAnalytics.activity?.length > 0 ? (
                        <div className="contact-analytics-timeline">
                          {contactAnalytics.activity.map((activity, index) => (
                            <div className="contact-analytics-event" key={`${activity.occurredAt}-${activity.type}-${index}`}>
                              <span className={`contact-analytics-event-icon ${activity.type}`} aria-hidden="true">
                                {activity.type === 'listing_view' ? '🏠' : activity.type === 'search' ? '⌕' : '↗'}
                              </span>
                              <span className="contact-analytics-event-detail">
                                {activity.type === 'listing_view' && activity.listingKey ? (
                                  <>Viewed property <a href={`/listing/${encodeURIComponent(activity.listingKey)}`}>{activity.listingKey}</a></>
                                ) : activity.type === 'search' ? (
                                  <>Searched{activity.searchTerm ? ` for “${activity.searchTerm}”` : ' listings'}</>
                                ) : (
                                  <>Viewed <span>{activity.pagePath || 'a page'}</span></>
                                )}
                              </span>
                              <time dateTime={activity.occurredAt}>
                                {new Date(activity.occurredAt).toLocaleString()}
                              </time>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="contact-analytics-message">No website activity was found in this period.</p>
                      )}
                    </>
                  )}
                </section>
              </div>
            </div>
          </div>
        )}
        <div className={`contact-progress-card ${selectedContact?.Email && !loadingProgress ? 'has-journey' : ''}`}>
          {(!selectedContact?.Email || loadingProgress) && <h3>Contact Progress</h3>}
          {!selectedContact || !selectedContact.Email ? (
            <p>Select a contact to view progress</p>
          ) : loadingProgress ? (
            <p>Loading progress...</p>
          ) : (
            <div className="progress-map">
              <section className="progress-container" aria-labelledby="client-journey-title">
                <div className="progress-header">
                  <span className="progress-eyebrow">Pipeline overview</span>
                  <h4 id="client-journey-title">Client Journey</h4>
                  <p>Track every milestone from first conversation to close.</p>
                </div>

                <div className="progress-bar-row">
                  <div
                    className="progress-bar"
                    role="progressbar"
                    aria-valuemin="0"
                    aria-valuemax={CLIENT_JOURNEY_STAGES.length}
                    aria-valuenow={completedJourneyStages}
                  >
                    <div className="progress-fill" style={{ width: `${journeyProgressPercent}%` }}></div>
                  </div>
                  <span className="progress-count" aria-label={`${completedJourneyStages} of ${CLIENT_JOURNEY_STAGES.length} stages complete`}>
                    {completedJourneyStages}/{CLIENT_JOURNEY_STAGES.length}
                  </span>
                </div>

                <ol className="stages-grid">
                  {CLIENT_JOURNEY_STAGES.map((stage, index) => {
                    const status = contactProgress?.[stage.key] || 'Pending';
                    const statusClass = normalizeJourneyStatus(status);
                    const note = contactProgress?.[`${stage.key}Note`];
                    const displayStatus = status === 'In-Progress' ? 'In progress' : status;

                    return (
                      <li key={stage.key} className={`stage-card ${statusClass}`}>
                        <button
                          type="button"
                          className="stage-card-button"
                          onClick={() => handleStageClick(stage.key)}
                          aria-label={`Update ${stage.label}. Current status: ${displayStatus}`}
                        >
                          <span className="stage-rail" aria-hidden="true"></span>
                          <span className="stage-icon-circle">
                            <JourneyStageIcon stageKey={stage.key} completed={statusClass === 'completed'} />
                          </span>
                          <span className="stage-content">
                            <span className="stage-number">Stage {String(index + 1).padStart(2, '0')}</span>
                            <span className="stage-title">{stage.label}</span>
                            <span className={`stage-badge ${statusClass}`}>
                              <span className="stage-badge-dot" aria-hidden="true"></span>
                              {displayStatus}
                            </span>
                            {note && <span className="stage-description">{note}</span>}
                          </span>
                          <span className="stage-edit-hint" aria-hidden="true">
                            <svg viewBox="0 0 24 24"><path d="M9 18h6m-5-4 7-7a2.1 2.1 0 0 0-3-3l-7 7-.5 3.5L10 14Z" /></svg>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ol>

                <p className="progress-footnote">Select a stage to update its status or add a note.</p>
              </section>
            </div>
          )}
        </div>
      </div>

      <div className="contact-details-container">
        <div className="chatleads-section">
          <div className="chatleads-card">
            <h3>Chat Leads ({chatLeads.length})</h3>
            <input
              type="text"
              placeholder="Search chat leads..."
              value={chatLeadSearch}
              onChange={(e) => setChatLeadSearch(e.target.value)}
              className="search-input"
            />

            {loadingChatLeads ? (
              <div className="loading-chatleads">Loading chat leads...</div>
            ) : (
              <div className="chatleads-list">
                {chatLeads.length === 0 ? (
                  <div className="empty-chatleads">
                    <p>No chat leads found</p>
                  </div>
                ) : (
                  chatLeads.filter(lead =>
                    lead.name?.toLowerCase().includes(chatLeadSearch.toLowerCase()) ||
                    lead.email?.toLowerCase().includes(chatLeadSearch.toLowerCase()) ||
                    lead.phoneNumber?.toLowerCase().includes(chatLeadSearch.toLowerCase()) ||
                    lead.postalCode?.toLowerCase().includes(chatLeadSearch.toLowerCase())
                  ).map((lead, index) => (
                    <div
                      key={index}
                      className={`chatlead-item ${selectedContact?.email === lead.email ? 'selected' : ''}`}
                      onClick={() => handleChatLeadClick(lead)}
                    >
                      <button
                        className="add-lead-btn"
                        onClick={(e) => handleAddLeadAsContact(e, lead)}
                        title="Add as contact"
                      >
                        ➕
                      </button>
                      <div className="chatlead-name">{lead.name}</div>
                      <div className="chatlead-details">
                        <div className="chatlead-contact">
                          {lead.email && <div>📧 {lead.email}</div>}
                          {lead.phoneNumber && <div>📞 {lead.phoneNumber}</div>}
                          {lead.postalCode && <div>📍 {lead.postalCode}</div>}
                        </div>
                        <div className="chatlead-service">
                          {lead.serviceReq && <div>🔧 {lead.serviceReq}</div>}
                          {lead.assignedAgentId && <div>Assigned to: {siteUsers.find(u => u.id === lead.assignedAgentId)?.username || 'Agent #' + lead.assignedAgentId}</div>}
                          {!lead.assignedAgentId && <div>Unassigned</div>}
                          {!isLead && <select
                            value={lead.assignedAgentId || ''}
                            onChange={async (e) => {
                              e.stopPropagation();
                              const agentId = e.target.value;
                              if (!agentId) return;
                              try {
                                await fetch('https://tophamtechbe.com/api/treas/idx/v1/assign-agent', {
                                  method: 'POST',
                                  headers: { 'X-API-Key': 'treas-api-key-2025', 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ type: 'chatLead', leadId: lead.id, agentId: Number(agentId), siteId: SITE_CONFIG.SITE_ID, overwrite: true })
                                });
                                fetchChatLeads();
                              } catch (err) { console.error('Error assigning agent:', err); }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="assign-agent-select"
                          >
                            <option value="">Assign Agent</option>
                            {siteUsers.map((user, i) => (
                              <option key={i} value={user.id}>{user.name || user.username}</option>
                            ))}
                          </select>}
                        </div>
                        {lead.createdAt && (
                          <div className="chatlead-date">
                            📅 {new Date(lead.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <div className="contact-detail-box">
          <h3>Contact Interests</h3>
          {!selectedContact ? (
            <p>Select a contact to view interests</p>
          ) : loadingInterests ? (
            <p>Loading interests...</p>
          ) : (
            <div className="interests-content">
              {contactInterests?.buying && contactInterests.buying.length > 0 && (
                <div>
                  <h4 style={{color: '#ffffff'}}>Buying Interests ({contactInterests.buying.length})</h4>
                  {contactInterests.buying.map((interest, index) => (
                    <div key={interest.id || index} className="interest-section">
                      <p><strong>Listing Key:</strong> <a href={`/listing/${interest.listingKey}`} style={{color: '#4a9eff'}}>{interest.listingKey}</a></p>
                      {interest.price && <p><strong>Price:</strong> ${Number(interest.price).toLocaleString()}</p>}
                      {interest.area && <p><strong>Area:</strong> {interest.area}</p>}
                      <p><strong>Date:</strong> {new Date(interest.datetime).toLocaleDateString()}</p>
                      {renderShowingControls(interest)}
                    </div>
                  ))}
                </div>
              )}

              {contactInterests?.selling && contactInterests.selling.length > 0 && (
                <div>
                  <h4 style={{color: '#ffffff'}}>Selling Interests ({contactInterests.selling.length})</h4>
                  {contactInterests.selling.map((interest, index) => (
                    <div
                      key={interest.id || index}
                      className={`interest-section ${interest.isSellingInquiry ? 'selling-inquiry' : ''}`}
                    >
                      {interest.isSellingInquiry && (
                        <p className="inquiry-note">📝 Gathered from chat</p>
                      )}
                      <p><strong>Property:</strong> {interest.address || interest.location}</p>
                      {interest.size && <p><strong>Size:</strong> {interest.size}</p>}
                      {interest.propertyDetails && <p><strong>Details:</strong> {interest.propertyDetails}</p>}
                      {interest.condition && <p><strong>Condition:</strong> {interest.condition}</p>}
                      {interest.timeline && <p><strong>Timeline:</strong> {interest.timeline}</p>}
                      <p><strong>Date:</strong> {new Date(interest.datetime || interest.created_at).toLocaleDateString()}</p>
                      <button
                        className="add-details-btn"
                        onClick={() => handleAddDetails(interest)}
                      >
                        {interest.isSellingInquiry ? '➕ Add Details' : '✏️ Update Details'}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {contactInterests?.chatLeads && contactInterests.chatLeads.length > 0 && (
                <div>
                  <h4 style={{color: '#ffffff'}}>Chat Leads ({contactInterests.chatLeads.length})</h4>
                  {contactInterests.chatLeads.map((lead, index) => (
                    <div key={lead.id || index} className="interest-section">
                      {lead.serviceReq && <p><strong>Service:</strong> {lead.serviceReq}</p>}
                      {lead.area && <p><strong>Area:</strong> {lead.area}</p>}
                      {lead.postalCode && <p><strong>Zip:</strong> {lead.postalCode}</p>}
                      {lead.price && <p><strong>Price:</strong> ${Number(lead.price).toLocaleString()}</p>}
                      {lead.listingKey && <p><strong>Listing:</strong> <a href={`/listing/${lead.listingKey}`} style={{color: '#4a9eff'}}>{lead.listingKey}</a></p>}
                      {lead.createdAt && <p><strong>Date:</strong> {new Date(lead.createdAt).toLocaleDateString()}</p>}
                    </div>
                  ))}
                </div>
              )}

              {(!contactInterests?.buying || contactInterests.buying.length === 0) &&
               (!contactInterests?.selling || contactInterests.selling.length === 0) &&
               (!contactInterests?.chatLeads || contactInterests.chatLeads.length === 0) && (
                <p>No interests found for this contact</p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="content-container">
        <div className="upload-section">
          <div className="upload-card">
            <div className="upload-icon">📋</div>
            <h3>Upload Contacts</h3>

            <div className="add-contact-section-main">
              <button
                onClick={() => setShowAddModal(true)}
                className="add-contact-btn-main"
              >
                ➕ Add Single Contact
              </button>
            </div>

            <div className="csv-section">
              <h4>Or Upload CSV File</h4>
              <p>Upload a CSV file with multiple contacts</p>

              <div className="upload-warning">
                ⚠️ Uploading a csv will replace your current contacts
              </div>

              <div className="csv-format">
                <h5>Required CSV Format:</h5>
                <div className="format-example">
                  <code>Name, Address1, Address2, City, State, Zip, CompanyName, PhoneNumber, Email, DateAdded, Group*, interest_area*, price_target*, assigned_agent*</code>
                </div>
                <div className="format-note">
                  <small><strong>DateAdded:</strong> MM/DD/YYYY (e.g., 01/15/2025)</small>
                  <small><strong>Group*:</strong> e.g. buyer, seller (optional)</small>
                  <small><strong>interest_area*:</strong> neighborhood e.g. Delray Beach, Boca Raton, Jupiter (optional)</small>
                  <small><strong>price_target*:</strong> price range e.g. 200000-300000 (optional)</small>
                  <small><strong>assigned_agent*:</strong> agent name e.g. craig (optional)</small>
                </div>
              </div>

              <div className="upload-area">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="file-input"
                  id="csv-upload"
                />
                <label htmlFor="csv-upload" className="upload-label">
                  <span className="upload-text">
                    {selectedFile ? '📄 ' + selectedFile.name : '📁 Choose CSV File'}
                  </span>
                </label>
              </div>

              {selectedFile && (
                <div className="action-area">
                  <button
                    onClick={handleUpload}
                    disabled={uploading}
                    className="upload-button"
                  >
                    {uploading ? '⏳ Uploading...' : '🚀 Upload Contacts'}
                  </button>
                </div>
              )}

              {uploadSuccess && (
                <div className="success-message">
                  ✅ Contacts uploaded successfully!
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="siteusers-section">
          <div className="siteusers-card">
            <h3>Site Users ({siteUsers.length})</h3>
            <input
              type="text"
              placeholder="Search site users..."
              value={siteUserSearch}
              onChange={(e) => setSiteUserSearch(e.target.value)}
              className="search-input"
            />

            {loadingSiteUsers ? (
              <div className="loading-siteusers">Loading site users...</div>
            ) : (
              <div className="siteusers-list">
                {siteUsers.length === 0 ? (
                  <div className="empty-siteusers">
                    <p>No site users found</p>
                  </div>
                ) : (
                  siteUsers.filter(user =>
                    user.username?.toLowerCase().includes(siteUserSearch.toLowerCase()) ||
                    user.email?.toLowerCase().includes(siteUserSearch.toLowerCase()) ||
                    user.roleName?.toLowerCase().includes(siteUserSearch.toLowerCase())
                  ).map((user, index) => (
                    <div
                      key={index}
                      className={`siteuser-item ${selectedContact?.email === user.email ? 'selected' : ''} clickable`}
                      onClick={() => handleSiteUserClick(user)}
                    >
                      <button
                        className="add-lead-btn"
                        onClick={(e) => handleAddUserAsContact(e, user)}
                        title="Add as contact"
                      >
                        ➕
                      </button>
                      <button
                        className="add-lead-btn"
                        onClick={(e) => toggleOnLeave(e, user)}
                        title={user.onLeave ? 'Mark as active' : 'Put on leave'}
                        style={{background: user.onLeave ? '#ff9800' : 'transparent', borderRadius: '4px', marginLeft: '4px'}}
                      >
                        {user.onLeave ? '🏖️' : '🟢'}
                      </button>
                      <div className="siteuser-name">{user.username} {user.onLeave && <span style={{fontSize: '11px', color: '#ff9800', marginLeft: '6px'}}>On Leave</span>}</div>
                      <div className="siteuser-details">
                        <div className="siteuser-info">
                          {user.email && <div>📧 {user.email}</div>}
                          {user.roleName && <div>👤 {user.roleName}</div>}
                        </div>
                        <div className="siteuser-meta">
                          {user.createdAt && <div>📅 {new Date(user.createdAt).toLocaleDateString()}</div>}
                          {user.siteId && <div>🏢 {user.siteId}</div>}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Contact</h3>
            <p>Are you sure you want to delete <strong>{contactToDelete?.Name}</strong>?</p>
            <p>This action cannot be undone.</p>
            <div className="modal-buttons">
              <button
                onClick={handleDeleteConfirm}
                className="delete-confirm-btn"
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="modal-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content add-contact-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Add New Contact</h3>
            <form className="add-contact-form">
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Name *"
                  value={newContact.Name}
                  onChange={(e) => setNewContact({...newContact, Name: e.target.value})}
                  className="form-input"
                  required
                />
                <input
                  type="email"
                  placeholder="Email *"
                  value={newContact.Email}
                  onChange={(e) => setNewContact({...newContact, Email: e.target.value})}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Company Name"
                  value={newContact.CompanyName}
                  onChange={(e) => setNewContact({...newContact, CompanyName: e.target.value})}
                  className="form-input"
                />
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={newContact.PhoneNumber}
                  onChange={(e) => setNewContact({...newContact, PhoneNumber: e.target.value})}
                  className="form-input"
                />
              </div>
              <input
                type="text"
                placeholder="Address Line 1"
                value={newContact.Address1}
                onChange={(e) => setNewContact({...newContact, Address1: e.target.value})}
                className="form-input"
              />
              <input
                type="text"
                placeholder="Address Line 2"
                value={newContact.Address2}
                onChange={(e) => setNewContact({...newContact, Address2: e.target.value})}
                className="form-input"
              />
              <div className="form-row">
                <input
                  type="text"
                  placeholder="City"
                  value={newContact.City}
                  onChange={(e) => setNewContact({...newContact, City: e.target.value})}
                  className="form-input"
                />
                <input
                  type="text"
                  placeholder="State"
                  value={newContact.State}
                  onChange={(e) => setNewContact({...newContact, State: e.target.value})}
                  className="form-input"
                  maxLength="2"
                />
                <input
                  type="text"
                  placeholder="Zip"
                  value={newContact.Zip}
                  onChange={(e) => setNewContact({...newContact, Zip: e.target.value})}
                  className="form-input"
                />
              </div>
              <input
                type="text"
                placeholder="Group (optional, e.g. buyer, seller)"
                value={newContact.Group}
                onChange={(e) => setNewContact({...newContact, Group: e.target.value})}
                className="form-input"
              />
              <select
                value={newContact.assigned_agent}
                onChange={(e) => setNewContact({...newContact, assigned_agent: e.target.value})}
                className="form-input"
              >
                <option value="">Select Assigned Agent (optional)</option>
                {siteUsers.map((user, i) => (
                  <option key={i} value={user.name || user.username}>{user.name || user.username}</option>
                ))}
              </select>
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Interest Area (e.g. Delray Beach)"
                  value={newContact.interest_area}
                  onChange={(e) => setNewContact({...newContact, interest_area: e.target.value})}
                  className="form-input"
                />
                <input
                  type="text"
                  placeholder="Price Target (e.g. 200000-300000)"
                  value={newContact.price_target}
                  onChange={(e) => setNewContact({...newContact, price_target: e.target.value})}
                  className="form-input"
                />
              </div>
            </form>
            <div className="modal-buttons">
              <button
                onClick={handleAddContact}
                className="add-confirm-btn"
                disabled={addingContact}
              >
                {addingContact ? 'Adding...' : 'Add Contact'}
              </button>
              <button
                onClick={() => setShowAddModal(false)}
                className="modal-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content add-contact-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Contact</h3>
            <form className="add-contact-form">
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Name *"
                  value={editContact.Name}
                  onChange={(e) => setEditContact({...editContact, Name: e.target.value})}
                  className="form-input"
                  required
                />
                <input
                  type="email"
                  placeholder="Email *"
                  value={editContact.Email}
                  onChange={(e) => setEditContact({...editContact, Email: e.target.value})}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Company Name"
                  value={editContact.CompanyName}
                  onChange={(e) => setEditContact({...editContact, CompanyName: e.target.value})}
                  className="form-input"
                />
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={editContact.PhoneNumber}
                  onChange={(e) => setEditContact({...editContact, PhoneNumber: e.target.value})}
                  className="form-input"
                />
              </div>
              <input
                type="text"
                placeholder="Address Line 1"
                value={editContact.Address1}
                onChange={(e) => setEditContact({...editContact, Address1: e.target.value})}
                className="form-input"
              />
              <input
                type="text"
                placeholder="Address Line 2"
                value={editContact.Address2}
                onChange={(e) => setEditContact({...editContact, Address2: e.target.value})}
                className="form-input"
              />
              <div className="form-row">
                <input
                  type="text"
                  placeholder="City"
                  value={editContact.City}
                  onChange={(e) => setEditContact({...editContact, City: e.target.value})}
                  className="form-input"
                />
                <input
                  type="text"
                  placeholder="State"
                  value={editContact.State}
                  onChange={(e) => setEditContact({...editContact, State: e.target.value})}
                  className="form-input"
                  maxLength="2"
                />
                <input
                  type="text"
                  placeholder="Zip"
                  value={editContact.Zip}
                  onChange={(e) => setEditContact({...editContact, Zip: e.target.value})}
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Interest Area (e.g. Delray Beach)"
                  value={editContact.interest_area}
                  onChange={(e) => setEditContact({...editContact, interest_area: e.target.value})}
                  className="form-input"
                />
                <input
                  type="text"
                  placeholder="Price Target (e.g. 200000-300000)"
                  value={editContact.price_target}
                  onChange={(e) => setEditContact({...editContact, price_target: e.target.value})}
                  className="form-input"
                />
              </div>
              <input
                type="text"
                placeholder="Group (e.g. buyer, seller)"
                value={editContact.Group || ''}
                onChange={(e) => setEditContact({...editContact, Group: e.target.value})}
                className="form-input"
              />
              <select
                value={editContact.assigned_agent || ''}
                onChange={(e) => setEditContact({...editContact, assigned_agent: e.target.value})}
                className="form-input"
              >
                <option value="">Select Assigned Agent</option>
                {siteUsers.map((user, i) => (
                  <option key={i} value={user.name || user.username}>{user.name || user.username}</option>
                ))}
              </select>
            </form>
            <div className="modal-buttons">
              <button
                onClick={handleUpdateContact}
                className="add-confirm-btn"
                disabled={updatingContact}
              >
                {updatingContact ? 'Updating...' : 'Update Contact'}
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                className="modal-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showNoteModal && (
        <div className="modal-overlay" onClick={() => setShowNoteModal(false)}>
          <div className="note-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Update {currentStage.charAt(0).toUpperCase() + currentStage.slice(1)} Status</h3>
            <p>Select status and add a note for the {currentStage.charAt(0).toUpperCase() + currentStage.slice(1)} stage:</p>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="status-dropdown"
            >
              <option value="In-Progress">In Progress</option>
              <option value="Blocked">Blocked</option>
              <option value="Completed">Completed</option>
            </select>

            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Enter your note here..."
              className="note-textarea"
              rows="4"
            />
            <div className="modal-buttons">
              <button
                onClick={handleUpdateStage}
                className="update-btn"
                disabled={updatingStage || !noteText.trim()}
              >
                {updatingStage ? 'Updating...' : `Update ${currentStage.charAt(0).toUpperCase() + currentStage.slice(1)}`}
              </button>
              <button
                onClick={() => {
                  setShowNoteModal(false);
                  setNoteText('');
                  setCurrentStage('');
                  setSelectedStatus('In-Progress');
                }}
                className="modal-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showScheduleModal && (
        <div className="modal-overlay" onClick={() => setShowScheduleModal(false)}>
          <div className="schedule-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{showingAction(contactInterests?.selectedBuying)}</h3>
            <p><strong>Contact:</strong> {selectedContact?.Name || selectedContact?.name}</p>
            <p><strong>Listing:</strong> {contactInterests?.selectedBuying?.listingKey}</p>

            {contactInterests?.selectedBuying?.showingTimezone && <p>Time zone: {contactInterests.selectedBuying.showingTimezone}. Confirm availability before scheduling.</p>}
            <div className="schedule-form">
              <div className="form-row">
                <input
                  type="date"
                  aria-label="Showing date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  className="schedule-input"
                  required
                />
                <input
                  type="time"
                  aria-label="Showing time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="schedule-input"
                  required
                />
                <button
                  className={`sms-toggle ${sendSMS ? 'active' : ''}`}
                  aria-label="Send client an SMS notification"
                  aria-pressed={sendSMS}
                  onClick={() => setSendSMS(!sendSMS)}
                  type="button"
                >
                  📱
                </button>
              </div>
              {sendSMS && (
                <p className="sms-note">✅ Client will receive SMS notification</p>
              )}
            </div>

            <div className="modal-buttons">
              <button
                onClick={async () => {
                  if (!scheduleDate || !scheduleTime) {
                    alert('Please select date and time');
                    return;
                  }

                  setSchedulingShowing(true);
                  try {
                    const datetime = `${scheduleDate} ${scheduleTime}`;
                    const selectedBuying = contactInterests.selectedBuying;
                    if (!Number.isInteger(Number(selectedBuying?.id)) || Number(selectedBuying.id) <= 0) {
                      throw new Error('Select a property interest before scheduling.');
                    }
                    const queryParams = new URLSearchParams({
                      id: selectedBuying.id,
                      datetime: datetime,
                      textClient: sendSMS.toString()
                    });

                    if (sendSMS) {
                      const phoneNumber = selectedContact?.PhoneNumber || selectedContact?.phoneNumber || selectedContact?.phone || '';
                      queryParams.append('clientPhone', phoneNumber);
                      // Get property address from API using listing key
                      let propertyAddress = 'Property Address';
                      try {
                        const propResponse = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?listingKey=${selectedBuying?.listingKey}`, {});
                        if (propResponse.ok) {
                          const propData = await propResponse.json();
                          const property = propData?.properties?.[0];
                          if (property) {
                            propertyAddress = formatPropertyAddress(property);
                          }
                        }
                      } catch (error) {
                        console.error('Error fetching property address:', error);
                      }
                      queryParams.append('address', propertyAddress);

                      if (!phoneNumber) {
                        alert('No phone number found for this contact. SMS cannot be sent.');
                        return;
                      }
                    }

                    const response = await fetch(`https://tophamtechbe.com/api/treas/idx/v1/schedule-showing?${queryParams}`, {
                      method: 'POST',
                      headers: {
                        'X-API-Key': 'treas-api-key-2025'
                      }
                    });

                    if (response.ok) {
                      setShowScheduleModal(false);
                      setScheduleDate('');
                      setScheduleTime('');
                      setSendSMS(false);
                      setShowSuccessModal(true);
                      await fetchContactInterests(selectedContact?.Email || selectedContact?.email || selectedBuying.email);
                    } else {
                      alert('Failed to schedule showing');
                    }
                  } catch (error) {
                    console.error('Error scheduling showing:', error);
                    alert('Failed to schedule showing');
                  } finally {
                    setSchedulingShowing(false);
                  }
                }}
                className="schedule-confirm-btn"
                disabled={schedulingShowing}
              >
                {schedulingShowing ? 'Saving...' : contactInterests?.selectedBuying?.showingRequestStatus === 'requested' ? 'Confirm Showing' : contactInterests?.selectedBuying?.scheduledShowing ? 'Save New Time' : 'Schedule Showing'}
              </button>
              <button
                onClick={() => {
                  setShowScheduleModal(false);
                  setScheduleDate('');
                  setScheduleTime('');
                  setSendSMS(false);
                }}
                className="modal-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div className="modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">✅</div>
            <h3>Showing Scheduled Successfully!</h3>
            <p>The showing has been saved. Contact Interests will refresh with the confirmed date and time.</p>
            <button
              className="success-close-btn"
              onClick={() => setShowSuccessModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {showSellingDetailsModal && (
        <div className="modal-overlay" onClick={() => setShowSellingDetailsModal(false)}>
          <div className="selling-details-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{isUpdating ? 'Update' : 'Add'} Selling Details</h3>
            <form className="selling-details-form">
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Name"
                  value={sellingDetailsForm.name}
                  onChange={(e) => setSellingDetailsForm({...sellingDetailsForm, name: e.target.value})}
                  className="form-input"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={sellingDetailsForm.email}
                  onChange={(e) => setSellingDetailsForm({...sellingDetailsForm, email: e.target.value})}
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={sellingDetailsForm.phoneNumber}
                  onChange={(e) => setSellingDetailsForm({...sellingDetailsForm, phoneNumber: e.target.value})}
                  className="form-input"
                />
                <input
                  type="text"
                  placeholder="Address"
                  value={sellingDetailsForm.address}
                  onChange={(e) => setSellingDetailsForm({...sellingDetailsForm, address: e.target.value})}
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Size"
                  value={sellingDetailsForm.size}
                  onChange={(e) => setSellingDetailsForm({...sellingDetailsForm, size: e.target.value})}
                  className="form-input"
                />
                <select
                  value={sellingDetailsForm.condition}
                  onChange={(e) => setSellingDetailsForm({...sellingDetailsForm, condition: e.target.value})}
                  className="form-input"
                >
                  <option value="">Select Condition</option>
                  <option value="Excellent">Excellent</option>
                  <option value="Good">Good</option>
                  <option value="Fair">Fair</option>
                  <option value="Needs Work">Needs Work</option>
                </select>
              </div>
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Timeline"
                  value={sellingDetailsForm.timeline}
                  onChange={(e) => setSellingDetailsForm({...sellingDetailsForm, timeline: e.target.value})}
                  className="form-input"
                />
                <select
                  value={sellingDetailsForm.status}
                  onChange={(e) => setSellingDetailsForm({...sellingDetailsForm, status: e.target.value})}
                  className="form-input"
                >
                  <option value="">Select Status</option>
                  <option value="Active">Active</option>
                  <option value="Pending">Pending</option>
                  <option value="Sold">Sold</option>
                </select>
              </div>
              <div className="listed-section">
                <input
                  type="checkbox"
                  id="listed-checkbox"
                  checked={sellingDetailsForm.listed}
                  onChange={(e) => setSellingDetailsForm({...sellingDetailsForm, listed: e.target.checked})}
                />
                <label htmlFor="listed-checkbox">Property is Listed</label>
              </div>
              <input
                type="text"
                placeholder="Listing Key (if listed)"
                value={sellingDetailsForm.listingKey}
                onChange={(e) => setSellingDetailsForm({...sellingDetailsForm, listingKey: e.target.value})}
                className="form-input"
              />
            </form>
            <div className="modal-buttons">
              <button
                onClick={handleSubmitSellingDetails}
                className="submit-details-btn"
                disabled={submittingDetails}
              >
                {submittingDetails ? 'Saving...' : (isUpdating ? 'Update' : 'Save') + ' Details'}
              </button>
              <button
                onClick={() => {
                  setShowSellingDetailsModal(false);
                  setIsUpdating(false);
                  setCurrentSellingInquiry(null);
                }}
                className="modal-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddSuccess && (
        <div className="add-success-notification">
          <div className="add-success-content">
            <span className="add-success-icon">✅</span>
            <span className="add-success-text">Successfully added to contacts!</span>
          </div>
        </div>
      )}

      {showOpenHouseSuccess && (
        <div className="add-success-notification">
          <div className="add-success-content">
            <span className="add-success-icon">🏠</span>
            <span className="add-success-text">Open house MLS key set successfully!</span>
          </div>
        </div>
      )}

      {showOpenHouseModal && (
        <div className="modal-overlay" onClick={() => setShowOpenHouseModal(false)}>
          <div className="open-house-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Set Open House MLS Key</h3>

            <div className="current-key-section">
              <label className="current-key-label">Current MLS Key:</label>
              {loadingCurrentKey ? (
                <span className="current-key-loading">Loading...</span>
              ) : (
                <span className="current-key-value">
                  {currentMlsKey || 'No key set'}
                </span>
              )}
            </div>

            <form className="open-house-form">
              <input
                type="text"
                placeholder="Enter new MLS Key"
                value={openHouseMlsKey}
                onChange={(e) => setOpenHouseMlsKey(e.target.value)}
                className="form-input"
                required
              />
              <div className="qr-code-section">
                <div className="qr-placeholder" onClick={() => {
                  setShowQRFullscreen(true);
                  setShowOpenHouseModal(false);
                }}>
                  <img
                    src="/open-house-qr.png"
                    alt="Open House QR Code"
                    className="qr-code-image"
                  />
                  <p>Open House QR Code</p>
                  <small>Click to view fullscreen</small>
                </div>
              </div>
            </form>
            <div className="modal-buttons">
              <button
                onClick={handleSubmitOpenHouse}
                className="submit-open-house-btn"
                disabled={submittingOpenHouse}
              >
                {submittingOpenHouse ? 'Setting...' : 'Set Open House'}
              </button>
              <button
                onClick={() => {
                  setShowOpenHouseModal(false);
                  setOpenHouseMlsKey('');
                }}
                className="modal-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showQRFullscreen && (
        <div className="qr-fullscreen-overlay" onClick={() => setShowQRFullscreen(false)}>
          <div className="qr-fullscreen-content" onClick={(e) => e.stopPropagation()}>
            <button className="qr-close-btn" onClick={() => setShowQRFullscreen(false)}>
              ×
            </button>
            <div className="qr-fullscreen-placeholder">
              <img
                src="/open-house-qr.png"
                alt="Open House QR Code"
                className="qr-code-fullscreen"
              />
              <p>Open House QR Code</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientManager;
